import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, deleteField, runTransaction, writeBatch, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { ownerStamp } from '../recordOwner';
import SessionDatePicker from './SessionDatePicker';
import { audienceFor } from '../noticeFanOut';
import { firstPendingStatus, logEntry } from '../noticeApproval';
import OleNotices from './OleNotices';
import NoticeApprovalSettings from './NoticeApprovalSettings';
import { useModuleAdmin } from '../useModuleAdmin';
import { fillTemplate, noticeValues } from '../noticeTemplate';
import Papa from 'papaparse';
import mammoth from 'mammoth';
import JSZip from 'jszip';
import { Layers, Settings as SettingsIcon, Plus, Trash2, Edit2, X, Users, Download, Upload, ChevronUp, ChevronDown, ChevronRight, ListFilter, ArrowUp, ArrowDown, Award, BookOpen, Crown, FileText, Printer, ClipboardCheck, ClipboardList, Check, HelpCircle, Copy } from 'lucide-react';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// Client-side-only identity for Settings list rows (never persisted to Firestore), used purely to detect
// "this row's code changed" vs "this is a brand-new row" when a Settings save needs to decide whether a
// code rename should cascade to existing ole_record/leader_record documents that reference the old code.
const generateRowId = () => `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const withRowIds = (arr) => arr.map(item => ({ ...item, _rowId: item._rowId || generateRowId() }));
const buildCodeMap = (arr) => Object.fromEntries(arr.map(item => [item._rowId, (item.code || '').trim()]));

const emptyForm = () => ({
  titleEn: '',
  titleZh: '',
  descriptionEn: '',
  descriptionZh: '',
  organization: '',
  nature: '',
  activityDate: '',
  // The schedule. activityDate stays as the FIRST date so every existing list, sort, filter and CSV
  // column keeps working -- rewriting all of those was not worth it, and one derived field is a
  // smaller thing to keep honest than thirty call sites.
  sessionDates: [],
  timeStart: '',
  timeEnd: '',
  venueEn: '',
  venueZh: '',
  staffInCharge: '',
  accompanyStaff: [],
  replyToStaff: '',
  replyDueDate: '',
});

// Generic search + checkbox multi-select with removable chips. Used for accompanying staff (small lists).
const TagPicker = ({ items, selected, onChange, labelFn, keyFn, placeholder }) => {
  const { t } = useT();
  const [queryText, setQueryText] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const filtered = useMemo(() => {
    const txt = queryText.trim().toLowerCase();
    const pool = txt ? items.filter(it => labelFn(it).toLowerCase().includes(txt)) : items;
    return pool.slice(0, 50);
  }, [items, queryText, labelFn]);

  const toggle = (key) => {
    const next = selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key];
    onChange(next);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
          {selected.map(key => {
            const item = items.find(it => keyFn(it) === key);
            return (
              <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)', borderRadius: '999px', padding: '0.2rem 0.5rem 0.2rem 0.65rem', fontSize: '0.75rem', fontWeight: '600' }}>
                {item ? labelFn(item) : key}
                <button type="button" onClick={() => toggle(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0 }}>
                  <X size={12} />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <input
        type="text"
        className="input-field"
        placeholder={placeholder}
        value={queryText}
        onFocus={() => setOpen(true)}
        onChange={e => { setQueryText(e.target.value); setOpen(true); }}
      />
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', zIndex: 200, maxHeight: '220px', overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{t('ole.noMatches')}</div>
          ) : filtered.map(it => {
            const key = keyFn(it);
            const isSel = selected.includes(key);
            return (
              <div
                key={key}
                onClick={() => toggle(key)}
                style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: isSel ? 'var(--color-primary-light)' : 'transparent' }}
              >
                <input type="checkbox" readOnly checked={isSel} style={{ width: '14px', height: '14px', pointerEvents: 'none' }} />
                {labelFn(it)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Editable code/name(EN/ZH) list used by the Settings tab (Organizations, Nature).
// Rows carry a numeric `seq` used to order both this table and the dropdowns built from it.
// Collapsed by default — Settings has grown to five of these lists, and most edits touch only one at a time.
const ListEditor = ({ title, items, onChange, fields, headerExtra, isMobile }) => {
  const { t } = useT();
  const [collapsed, setCollapsed] = useState(true);

  const addRow = () => {
    const maxSeq = items.reduce((m, it) => Math.max(m, Number(it.seq) || 0), 0);
    onChange([...items, { ...Object.fromEntries(fields.map(f => [f.key, ''])), seq: maxSeq + 10, _rowId: generateRowId() }]);
  };
  const updateRow = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));

  const sortedIndices = items
    .map((_, i) => i)
    .sort((a, b) => (Number(items[a].seq) || 0) - (Number(items[b].seq) || 0));

  // Renumbers everyone 10/20/30/... in the new order, rather than swapping raw seq values —
  // rows with equal/blank seq (e.g. legacy data saved before this field existed) would otherwise
  // stay tied forever since swapping two equal values is a no-op.
  const moveRow = (idx, direction) => {
    const pos = sortedIndices.indexOf(idx);
    const swapPos = pos + direction;
    if (swapPos < 0 || swapPos >= sortedIndices.length) return;
    const newOrder = [...sortedIndices];
    [newOrder[pos], newOrder[swapPos]] = [newOrder[swapPos], newOrder[pos]];
    const next = [...items];
    newOrder.forEach((originalIdx, i) => {
      next[originalIdx] = { ...next[originalIdx], seq: (i + 1) * 10 };
    });
    onChange(next);
  };

  return (
    <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '1rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          {collapsed ? <ChevronRight size={18} color="var(--color-text-muted)" /> : <ChevronDown size={18} color="var(--color-text-muted)" />}
          <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{title}</h3>
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', fontWeight: '400' }}>({items.length})</span>
        </button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {headerExtra}
          <button type="button" className="btn-outline" onClick={addRow} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', whiteSpace: 'nowrap' }}>
            <Plus size={16} /> Add
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="table-container" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ backgroundColor: 'var(--color-bg-app)' }}>
              <tr>
                <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)', width: '130px' }}>{t('ole.seq')}</th>
                {fields.map(f => <th key={f.key} style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{f.label}</th>)}
                <th style={{ width: '48px' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={fields.length + 2} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('ole.noItems')}</td></tr>
              ) : sortedIndices.map((idx, pos) => {
                const item = items[idx];
                return (
                  <tr key={idx} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <input
                          type="number"
                          className="input-field"
                          value={item.seq ?? ''}
                          onChange={e => updateRow(idx, 'seq', e.target.value)}
                          style={{ width: '60px' }}
                        />
                        <button type="button" onClick={() => moveRow(idx, -1)} disabled={pos === 0} title={t('ole.moveUp')} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: pos === 0 ? 'default' : 'pointer', opacity: pos === 0 ? 0.35 : 1, display: 'flex', padding: '2px' }}>
                          <ChevronUp size={14} />
                        </button>
                        <button type="button" onClick={() => moveRow(idx, 1)} disabled={pos === sortedIndices.length - 1} title={t('ole.moveDown')} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: pos === sortedIndices.length - 1 ? 'default' : 'pointer', opacity: pos === sortedIndices.length - 1 ? 0.35 : 1, display: 'flex', padding: '2px' }}>
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </td>
                    {fields.map(f => (
                      <td key={f.key} style={{ padding: '0.4rem 0.5rem' }}>
                        {f.type === 'select' ? (
                          <select className="input-field" value={item[f.key] || ''} onChange={e => updateRow(idx, f.key, e.target.value)} style={{ minWidth: '160px' }}>
                            <option value="">-- Select --</option>
                            {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input className="input-field" value={item[f.key] || ''} placeholder={f.placeholder} onChange={e => updateRow(idx, f.key, e.target.value)} style={{ minWidth: '120px' }} />
                        )}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" onClick={() => removeRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CSV_COLUMNS = ['activityId', 'schoolYear', 'titleEn', 'titleZh', 'descriptionEn', 'descriptionZh', 'organization', 'nature', 'activityDate', 'timeStart', 'timeEnd', 'venueEn', 'venueZh', 'staffInCharge', 'accompanyStaff', 'replyToStaff', 'replyDueDate', 'studentInvolved'];

// Reference list shown next to each template's preview — these are the tags (typed as {key} in Word)
// that a future "issue documents" merge step would substitute with real data. Purely informational here.
// Only the tags. The label for each is `ole.variables.<key>` and is looked up at render --
// a module-level constant is built before there is a language to build it in.
const TEMPLATE_VARIABLE_KEYS = [
  'activityTitleEn',
  'activityTitleZh',
  'activityDescriptionEn',
  'activityDescriptionZh',
  'organizationEn',
  'organizationZh',
  'natureEn',
  'natureZh',
  'activityDate',
  'timeStart',
  'timeEnd',
  'venueEn',
  'venueZh',
  'staffInCharge',
  'staffInChargeZh',
  'replyToStaff',
  'replyToStaffZh',
  'replyDueDate',
  'schoolYear',
  'studentName',
  'studentChineseName',
  'sid',
  'currentClass',
  'classNumber',
  'roleEn',
  'roleZh',
  'achievement',
];

const ALL_PARTICIPANTS_VALUE = '__all__';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Combines several merged .docx Blobs (one per participant, all from the same template) into a single
// .docx -- each participant's body content, minus its own trailing <w:sectPr> (section/page-size
// properties), joined by an explicit page-break paragraph, with one shared <w:sectPr> kept at the very
// end. All other parts (styles, headers/footers, media, rels) come from the first participant's copy,
// since every copy shares the same template and therefore the same non-body parts.
const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

const combineDocxBlobs = async (results) => {
  if (results.length === 1) return results[0].blob;

  const zips = await Promise.all(results.map(r => JSZip.loadAsync(r.blob)));
  const documentXmls = await Promise.all(zips.map(z => z.file('word/document.xml').async('string')));

  // Real DOM manipulation, not regex/string slicing -- a first pass tried locating each participant's
  // trailing <w:sectPr> with a regex anchored to the end of the body string and slicing it off, but a
  // document's final section properties are just as often stored NESTED inside the last <w:p>'s <w:pPr>
  // as they are a plain sibling of the paragraphs. Slicing text at that position left a dangling,
  // unclosed <w:pPr>/<w:p> for any participant using the nested form, corrupting the file (confirmed via
  // DOMParser reporting "Opening and ending tag mismatch: pPr" and Word refusing to open it). Manipulating
  // real DOM nodes and removing the actual <w:sectPr> element (wherever it lives) guarantees well-formed
  // output regardless of which pattern a given participant's copy happens to use.
  const parser = new DOMParser();
  const serializer = new XMLSerializer();

  // A fresh, independent parse used only as the shell to build combined content into -- kept separate
  // from the per-participant "source" parses below so clearing its body doesn't also empty the data
  // participant 0 is read from.
  const outputDoc = parser.parseFromString(documentXmls[0], 'application/xml');
  const outputBody = outputDoc.getElementsByTagNameNS(W_NS, 'body')[0];
  while (outputBody.firstChild) outputBody.removeChild(outputBody.firstChild);

  let sharedSectPrEl = null;

  documentXmls.forEach((xml, i) => {
    const sourceDoc = parser.parseFromString(xml, 'application/xml');
    const sourceBody = sourceDoc.getElementsByTagNameNS(W_NS, 'body')[0];

    // Detach this participant's own final section properties, whether a direct <w:body> child or
    // nested inside the last <w:p>'s <w:pPr> -- both are valid OOXML for a document's last section.
    let sectPrEl = Array.from(sourceBody.children).find(c => c.localName === 'sectPr') || null;
    if (sectPrEl) {
      sourceBody.removeChild(sectPrEl);
    } else {
      const paragraphs = Array.from(sourceBody.children).filter(c => c.localName === 'p');
      const lastP = paragraphs[paragraphs.length - 1];
      const pPr = lastP && Array.from(lastP.children).find(c => c.localName === 'pPr');
      const nestedSectPr = pPr && Array.from(pPr.children).find(c => c.localName === 'sectPr');
      if (nestedSectPr) {
        sectPrEl = nestedSectPr;
        pPr.removeChild(nestedSectPr);
      }
    }
    if (i === documentXmls.length - 1 && sectPrEl) sharedSectPrEl = sectPrEl;

    if (i > 0) {
      const pageBreakP = outputDoc.createElementNS(W_NS, 'w:p');
      const run = outputDoc.createElementNS(W_NS, 'w:r');
      const br = outputDoc.createElementNS(W_NS, 'w:br');
      br.setAttributeNS(W_NS, 'w:type', 'page');
      run.appendChild(br);
      pageBreakP.appendChild(run);
      outputBody.appendChild(pageBreakP);
    }
    Array.from(sourceBody.children).forEach(child => {
      outputBody.appendChild(outputDoc.importNode(child, true));
    });
  });

  if (sharedSectPrEl) {
    outputBody.appendChild(outputDoc.importNode(sharedSectPrEl, true));
  }

  // outputDoc was parsed from a string that already had its own <?xml ...?> prolog, and some browsers'
  // XMLSerializer re-emits that prolog on serialization -- strip whatever it produces before prepending
  // exactly one declaration ourselves, or a stray second declaration mid-file corrupts the XML.
  let combinedXml = serializer.serializeToString(outputDoc).replace(/^\s*<\?xml[^>]*\?>\s*/, '');
  combinedXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n' + combinedXml;

  const baseZip = zips[0];
  baseZip.file('word/document.xml', combinedXml);
  return baseZip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
};

const escapeXmlText = (str) => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Builds a minimal but valid .docx (via JSZip, no server round-trip) listing every {tag} this module
// supports, so staff have a working file to open in Word and lay out rather than starting from a blank page.
const buildSampleTemplateDocx = async (tr) => {
  const zip = new JSZip();
  zip.file('[Content_Types].xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>');
  zip.folder('_rels').file('.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>');

  const titlePara = `<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r><w:rPr><w:b/><w:sz w:val="32"/></w:rPr><w:t>${escapeXmlText(tr('ole.sampleDocTitle'))}</w:t></w:r></w:p>`;
  const introPara = `<w:p><w:r><w:t xml:space="preserve">${escapeXmlText(tr('ole.sampleDocIntro'))}</w:t></w:r></w:p>`;
  const spacer = '<w:p/>';
  // The tag stays English -- it has to match what mergeDocxTemplate looks for. Only the line
  // explaining what each tag fills is translated.
  const varParas = TEMPLATE_VARIABLE_KEYS.map(key =>
    `<w:p><w:r><w:rPr><w:b/></w:rPr><w:t>{${escapeXmlText(key)}}</w:t></w:r><w:r><w:t xml:space="preserve"> - ${escapeXmlText(tr(`ole.variables.${key}`))}</w:t></w:r></w:p>`
  ).join('');

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${titlePara}${spacer}${introPara}${spacer}${varParas}</w:body></w:document>`;
  zip.folder('word').file('document.xml', documentXml);

  return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
};

const handleDownloadSampleTemplate = async (tr) => {
  try {
    const blob = await buildSampleTemplateDocx(tr);
    downloadBlob(blob, 'OLE_Template_Sample.docx');
  } catch (e) {
    console.error('Error generating sample template', e);
    alert(tr('ole.sampleFailed', { message: e.message }));
  }
};

const decodeXmlEntities = (str) => String(str)
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&apos;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/&amp;/g, '&');

// Fills a template .docx's {tag} placeholders with real values and returns a new .docx Blob. Works at
// paragraph granularity: for each <w:p>, concatenate all its <w:t> runs into plain text (Word frequently
// splits a paragraph's text across multiple runs for its own formatting/spellcheck reasons, so a naive
// whole-XML string replace would miss any tag split across a run boundary), substitute on that
// concatenated text, then -- only if a substitution actually happened -- collapse the paragraph back down
// to a single run (keeping its first run's formatting) carrying the merged text. Paragraphs with no tags
// are left byte-for-byte untouched, so their original per-run formatting survives.
const mergeDocxTemplate = async (arrayBuffer, values) => {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const docPath = 'word/document.xml';
  const docFile = zip.file(docPath);
  if (!docFile) throw new Error('Not a valid Word document (missing word/document.xml).');
  const xml = await docFile.async('string');

  // Case-insensitive so a template author typing {Organization} or {ORGANIZATION} still resolves to the
  // 'organization' key rather than being left as a literal, unreplaced tag.
  const lowerValues = Object.fromEntries(Object.entries(values).map(([k, v]) => [k.toLowerCase(), v]));

  const mergedXml = xml.replace(/<w:p\b[^>]*>[\s\S]*?<\/w:p>/g, (paragraphXml) => {
    // \b after w:t is required -- without it this also matches <w:tab>, <w:tabs>, <w:tbl>, <w:tc>, <w:tr>
    // etc. (any tag whose name has "w:t" as a prefix), swallowing everything up to the next real </w:t>
    // as if it were this run's text. The <w:tab\s*\/> alternative keeps a real tab character (a
    // horizontal-tab run, common for label/value pairs on one line) from silently disappearing.
    const partMatches = [...paragraphXml.matchAll(/<w:t\b[^>]*>([\s\S]*?)<\/w:t>|<w:tab\s*\/>/g)];
    if (partMatches.length === 0) return paragraphXml;
    const fullText = partMatches.map(m => m[1] !== undefined ? decodeXmlEntities(m[1]) : '\t').join('');
    if (!/\{[a-zA-Z]+\}/.test(fullText)) return paragraphXml;

    const replacedText = fullText.replace(/\{([a-zA-Z]+)\}/g, (match, key) => {
      const lookupKey = key.toLowerCase();
      return Object.prototype.hasOwnProperty.call(lowerValues, lookupKey) ? String(lowerValues[lookupKey] ?? '') : match;
    });

    const pPrMatch = paragraphXml.match(/<w:pPr>[\s\S]*?<\/w:pPr>/);
    const pPr = pPrMatch ? pPrMatch[0] : '';
    const withoutPPr = pPrMatch ? paragraphXml.replace(pPrMatch[0], '') : paragraphXml;
    const rPrMatch = withoutPPr.match(/<w:rPr>[\s\S]*?<\/w:rPr>/);
    const rPr = rPrMatch ? rPrMatch[0] : '';

    return `<w:p>${pPr}<w:r>${rPr}<w:t xml:space="preserve">${escapeXmlText(replacedText)}</w:t></w:r></w:p>`;
  });

  zip.file(docPath, mergedXml);
  return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
};

// Combined sort + filter control for a table column header: a small icon opens a portal-rendered
// popup (same pattern as TalentBank's MultiSelectDropdown) with sort direction buttons and a filter textbox,
// so the header row doesn't need a permanently-visible filter input per column.
const ColumnMenu = ({ label, filterValue, onFilterChange, sortDirection, onSort }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        (!panelRef.current || !panelRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const toggleOpen = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelStyle({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  };

  const isActive = !!filterValue.trim() || !!sortDirection;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        title={t('ole.sortFilter')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: '2px', color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)', flexShrink: 0 }}
      >
        <ListFilter size={14} />
      </button>
      {open && panelStyle && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed', top: panelStyle.top, left: panelStyle.left, width: '220px',
            backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)', zIndex: 9999, padding: '0.75rem',
          }}
        >
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>{t('ole.sort')}</div>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem' }}>
            <button
              type="button"
              onClick={() => onSort(sortDirection === 'asc' ? null : 'asc')}
              className="btn-outline"
              style={{ flex: 1, padding: '0.3rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', backgroundColor: sortDirection === 'asc' ? 'var(--color-primary-light)' : undefined, color: sortDirection === 'asc' ? 'var(--color-primary)' : undefined }}
            >
              <ArrowUp size={12} /> {t('ole.asc')}
            </button>
            <button
              type="button"
              onClick={() => onSort(sortDirection === 'desc' ? null : 'desc')}
              className="btn-outline"
              style={{ flex: 1, padding: '0.3rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', backgroundColor: sortDirection === 'desc' ? 'var(--color-primary-light)' : undefined, color: sortDirection === 'desc' ? 'var(--color-primary)' : undefined }}
            >
              <ArrowDown size={12} /> {t('ole.desc')}
            </button>
          </div>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>{t('ole.filter')}</div>
          <input
            type="text"
            className="input-field"
            placeholder={t('ole.filterPlaceholder', { label })}
            value={filterValue}
            onChange={e => onFilterChange(e.target.value)}
            style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem', width: '100%', boxSizing: 'border-box' }}
            autoFocus
          />
        </div>,
        document.body
      )}
    </>
  );
};

const OLEManagement = () => {
  const { t } = useT();
  const { activeSchoolId, isMobile, closeSidebar } = useOutletContext();

  const [activeTab, setActiveTab] = useState('activity');
  // Who may raise an activity notice and sign one off, from the same delegation document every
  // other module's admins live in.
  const canManageOle = useModuleAdmin(activeSchoolId, 'ole_management');
  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');

  const [organizations, setOrganizations] = useState([]);
  const [natures, setNatures] = useState([]);
  const [participantRoles, setParticipantRoles] = useState([]);
  const [leaderRoles, setLeaderRoles] = useState([]);
  // Snapshot of { _rowId: code } per list, taken whenever settings are (re)loaded from Firestore — diffed
  // against current codes on save to detect renames that need to cascade to existing records.
  const originalCodesRef = useRef({ organizations: {}, natures: {}, participantRoles: {}, leaderRoles: {} });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [importCandidates, setImportCandidates] = useState([]);

  const [staffList, setStaffList] = useState([]);
  const [studentList, setStudentList] = useState([]);

  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const [showModal, setShowModal] = useState(false);
  // Dates that already have attendance recorded. Removing one would orphan those records, so the
  // picker refuses -- a day somebody has been marked present for is a fact, not a plan.
  const [lockedSessionDates, setLockedSessionDates] = useState([]);
  const [copySource, setCopySource] = useState(null);
  // Issuing the reply slip from here is what removes the retyping: the activity already knows its
  // dates and its participants, and staff were copying both into Notice & Payment by hand.
  const [noticeSource, setNoticeSource] = useState(null);
  const [noticeContent, setNoticeContent] = useState('');
  const [noticeIssuer, setNoticeIssuer] = useState('');
  const [issuingNotice, setIssuingNotice] = useState(false);
  const [noticeTemplates, setNoticeTemplates] = useState([]);
  const [copyParticipants, setCopyParticipants] = useState(true);
  const [copyRoles, setCopyRoles] = useState(true);
  const [copying, setCopying] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [participantActivity, setParticipantActivity] = useState(null);
  const [participantSelected, setParticipantSelected] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantClassFilter, setParticipantClassFilter] = useState('');
  const [savingParticipants, setSavingParticipants] = useState(false);

  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleActivity, setRoleActivity] = useState(null);
  const [roleAssignments, setRoleAssignments] = useState({});
  const [savingRoleAssignments, setSavingRoleAssignments] = useState(false);

  const [portfolioClass, setPortfolioClass] = useState('');
  const [portfolioStudentSid, setPortfolioStudentSid] = useState('');
  const [portfolioActivities, setPortfolioActivities] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [expandedPortfolioIds, setExpandedPortfolioIds] = useState(new Set());

  const [leaderRecords, setLeaderRecords] = useState([]);
  const [loadingLeaders, setLoadingLeaders] = useState(false);
  const [showLeaderModal, setShowLeaderModal] = useState(false);
  const [editingLeaderId, setEditingLeaderId] = useState(null);
  const [leaderForm, setLeaderForm] = useState({ organization: '', role: '', sid: '' });
  const [leaderClassFilter, setLeaderClassFilter] = useState('');
  const [savingLeader, setSavingLeader] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', file: null });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Print/issue flow: pick an activity (+ optional participant) for a template, merge real data into
  // the uploaded .docx server-side (via the getTemplateFile Cloud Function, see [[ole_management_module]]
  // memory for why), and preview/download the merged result -- replaces the old raw-file preview, which
  // could never render because reading the file client-side hit the same Storage CORS gap.
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Whether each participant turned up to the activity. Kept in its own map rather than inside
  // participantDetails so the two features stay independent of each other's save paths.
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceActivity, setAttendanceActivity] = useState(null);
  const [attendanceMap, setAttendanceMap] = useState({});
  // Attendance is taken one session at a time. An activity that runs five days has five registers,
  // and "attended three of five" is the only useful answer for it -- which cannot be said at all if
  // attendance hangs off the activity.
  const [attendanceSessions, setAttendanceSessions] = useState([]);
  const [attendanceSessionId, setAttendanceSessionId] = useState('');
  const [savingAttendance, setSavingAttendance] = useState(false);

  // Shared dialog behaviour for every modal in this module: retract the mobile sidebar (a fixed
  // overlay that would otherwise sit on top of the sheet), stop the page behind from scrolling, and
  // close on Escape. Participant/Role sit at a higher z-index and can stack over the activity form,
  // so Escape dismisses those first rather than tearing down the form underneath them.
  const anyModalOpen = showModal || showParticipantModal || showRoleModal
    || showLeaderModal || showTemplateModal || showPrintModal || showAttendanceModal;

  useEffect(() => {
    if (!anyModalOpen) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showParticipantModal || showRoleModal) {
        setShowParticipantModal(false);
        setShowRoleModal(false);
        return;
      }
      setShowModal(false);
      setShowLeaderModal(false);
      setShowTemplateModal(false);
      setShowPrintModal(false);
      setShowAttendanceModal(false);
    };

    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [anyModalOpen, isMobile, closeSidebar, showParticipantModal, showRoleModal]);
  const [printTemplate, setPrintTemplate] = useState(null);
  const [printActivityId, setPrintActivityId] = useState('');
  const [printParticipantSid, setPrintParticipantSid] = useState('');
  const [merging, setMerging] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [printError, setPrintError] = useState('');
  const [mergedBlob, setMergedBlob] = useState(null);
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState('');

  // --- Reference data: academic years, staff, students, module settings ---
  useEffect(() => {
    if (!activeSchoolId) return;
    const fetchReference = async () => {
      try {
        const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const current = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSchoolYear(current.yearId);
        }
      } catch (e) {
        console.error('Error loading academic years', e);
      }

      try {
        const staffSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const sList = [];
        staffSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          sList.push({
            id: d.id,
            initial: data.initial,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial,
            chineseName: data.chineseName || '',
          });
        });
        sList.sort((a, b) => a.name.localeCompare(b.name));
        setStaffList(sList);
      } catch (e) {
        console.error('Error loading staff list', e);
      }

      try {
        const studentSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        const stList = [];
        studentSnap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          stList.push({
            sid: data.sid,
            // Records are keyed by email -- the only identifier a security rule can verify against
            // the caller. The sid and class fields below stay for display, never for identity.
            email: (data.email || d.id || '').toLowerCase(),
            guardianEmails: [data.guardianEmail, data.guardianEmail2]
              .filter(Boolean)
              .map(e => String(e).toLowerCase()),
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown',
            chineseName: data.chineseName || '',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
          });
        });
        stList.sort((a, b) => (a.currentClass || '').localeCompare(b.currentClass || '', undefined, { numeric: true }) || Number(a.classNumber || 0) - Number(b.classNumber || 0));
        setStudentList(stList);
      } catch (e) {
        console.error('Error loading student list', e);
      }

      try {
        setSettingsLoaded(false);
        const settingsSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'ole_settings'));
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          const orgs = withRowIds(Array.isArray(data.organizations) ? data.organizations : []);
          const nats = withRowIds(Array.isArray(data.natures) ? data.natures : []);
          const pRoles = withRowIds(Array.isArray(data.participantRoles) ? data.participantRoles : []);
          const lRoles = withRowIds(Array.isArray(data.leaderRoles) ? data.leaderRoles : []);
          setOrganizations(orgs);
          setNatures(nats);
          setParticipantRoles(pRoles);
          setLeaderRoles(lRoles);
          originalCodesRef.current = {
            organizations: buildCodeMap(orgs),
            natures: buildCodeMap(nats),
            participantRoles: buildCodeMap(pRoles),
            leaderRoles: buildCodeMap(lRoles),
          };
        } else {
          setOrganizations([]);
          setNatures([]);
          setParticipantRoles([]);
          setLeaderRoles([]);
          originalCodesRef.current = { organizations: {}, natures: {}, participantRoles: {}, leaderRoles: {} };
        }
        setSettingsLoaded(true);
      } catch (e) {
        console.error('Error loading OLE settings', e);
        setSettingsLoaded(true);
      }

      try {
        const systemSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
        if (systemSnap.exists()) {
          const dropdowns = systemSnap.data().dropdowns || {};
          const toCandidate = (item) => ({
            code: item.code || '',
            nameEn: item.nameEn || item.name || item.code || '',
            nameZh: item.nameZh || '',
          });
          const departments = (dropdowns.departments || []).map(toCandidate);
          const committees = (dropdowns.inventoryCommittee || []).map(toCandidate);
          setImportCandidates([...departments, ...committees].filter(c => c.code));
        }
      } catch (e) {
        console.error('Error loading department/committee parameters', e);
      }
    };
    fetchReference();
  }, [activeSchoolId]);

  const classOptions = useMemo(() => [...new Set(studentList.map(s => s.currentClass))].filter(Boolean).sort(), [studentList]);

  // --- Activities for the selected school year ---
  const fetchActivities = async () => {
    if (!activeSchoolId || !schoolYear) { setActivities([]); return; }
    setLoadingActivities(true);
    try {
      const q = query(collection(db, 'schools', activeSchoolId, 'ole_record'), where('schoolYear', '==', schoolYear));
      const snap = await getDocs(q);
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.activityDate || '').localeCompare(a.activityDate || '') || (b.activityId || '').localeCompare(a.activityId || ''));
      setActivities(list);
      return list;
    } catch (e) {
      console.error('Error loading activities', e);
    } finally {
      setLoadingActivities(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId, schoolYear]);

  // The school's central notice wording. Same collection Notice & Payment maintains -- an activity
  // notice and a general notice should not read differently just because of where they were issued.
  useEffect(() => {
    if (!activeSchoolId) return undefined;
    return onSnapshot(
      collection(db, 'schools', activeSchoolId, 'notice_template'),
      snap => setNoticeTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => console.error('Error loading notice templates', e),
    );
  }, [activeSchoolId]);

  const organizationMap = useMemo(() => Object.fromEntries(organizations.map(o => [o.code, o])), [organizations]);
  const natureMap = useMemo(() => Object.fromEntries(natures.map(n => [n.code, n])), [natures]);
  const staffMap = useMemo(() => Object.fromEntries(staffList.map(s => [s.initial, s])), [staffList]);
  const studentMap = useMemo(() => Object.fromEntries(studentList.map(s => [s.sid, s])), [studentList]);

  // The SID list drives every existing filter and display; the email lists are what the security
  // rule tests, since a rule cannot resolve a SID to an account. Both are written together so they
  // can never disagree about who is on an activity.
  // Writes one row per (activity, student) into ole_participant. The activity document keeps the
  // roster for staff; these rows are what a family reads, and each names one student only.
  //
  // Also removes the wholesale-overwrite trap: role and attendance now live on the student's own
  // row, so saving one no longer rewrites a dictionary keyed by everyone else.
  // The other half of the fan-out, and the half that was missing entirely: rows are written for
  // whoever is on the activity now, but nothing ever deleted the row of somebody taken off it. An
  // ole_participant row is what a FAMILY reads -- so a student removed from an activity, and every
  // student on an activity that was deleted outright, went on seeing it in their portfolio with no
  // staff-side record left to find it from.
  const removeParticipantRows = async (activityId, sids) => {
    const chunk = 400; // Firestore caps a batch at 500 writes.
    for (let i = 0; i < sids.length; i += chunk) {
      const batch = writeBatch(db);
      sids.slice(i, i + chunk).forEach(sid => {
        batch.delete(doc(db, 'schools', activeSchoolId, 'ole_participant', `${activityId}__${sid}`));
      });
      await batch.commit();
    }
  };

  // Every row an activity has, however it was created -- used when the activity itself goes. Read
  // rather than derived from studentInvolved, because a row left behind by an earlier removal is
  // exactly the thing being cleaned up and would not appear in that list.
  const allParticipantRowIds = async (activityId) => {
    const snap = await getDocs(query(
      collection(db, 'schools', activeSchoolId, 'ole_participant'),
      where('activityId', '==', activityId),
    ));
    return snap.docs.map(d => d.id);
  };

  const fanOutParticipants = async (activity, sids, opts = {}) => {
    const roles = opts.roles || activity.participantDetails || {};
    const attend = opts.attendance || activity.attendance || {};
    const chunk = 400; // Firestore caps a batch at 500 writes.
    for (let i = 0; i < sids.length; i += chunk) {
      const batch = writeBatch(db);
      sids.slice(i, i + chunk).forEach(sid => {
        const st = studentMap[sid];
        batch.set(
          doc(db, 'schools', activeSchoolId, 'ole_participant', `${activity.id}__${sid}`),
          {
            activityId: activity.id,
            sid,
            studentEmail: st?.email || '',
            // Always written, even empty -- a missing field makes the read rule error, and an
            // erroring rule denies.
            guardianEmails: st?.guardianEmails || [],
            // Denormalised so a student's portfolio and any export are a single collection read.
            titleEn: activity.titleEn || '',
            titleZh: activity.titleZh || '',
            activityDate: activity.activityDate || '',
            nature: activity.nature || '',
            organization: activity.organization || '',
            schoolYear: activity.schoolYear || '',
            role: roles[sid]?.role || '',
            achievement: roles[sid]?.achievement || '',
            attendance: attend[sid] || 'pending',
          },
          { merge: true },
        );
      });
      await batch.commit();
    }
  };

  const emailsForSids = (sids) => {
    const students = new Set();
    const guardians = new Set();
    (sids || []).forEach(sid => {
      const st = studentMap[sid];
      if (!st) return;
      if (st.email) students.add(st.email);
      (st.guardianEmails || []).forEach(g => guardians.add(g));
    });
    return { studentEmails: [...students], guardianEmails: [...guardians] };
  };
  const roleMap = useMemo(() => Object.fromEntries(participantRoles.map(r => [r.code, r])), [participantRoles]);
  const leaderRoleMap = useMemo(() => Object.fromEntries(leaderRoles.map(r => [r.code, r])), [leaderRoles]);

  const bySeq = (a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0);
  const sortedOrganizations = useMemo(() => [...organizations].sort(bySeq), [organizations]);
  const sortedNatures = useMemo(() => [...natures].sort(bySeq), [natures]);
  const sortedParticipantRoles = useMemo(() => [...participantRoles].sort(bySeq), [participantRoles]);
  const sortedLeaderRoles = useMemo(() => [...leaderRoles].sort(bySeq), [leaderRoles]);

  const staffOptions = useMemo(() => staffList.map(s => ({ value: s.initial, label: `${s.name} (${s.initial})` })), [staffList]);

  const bilingualLabel = (item) => item ? (item.nameZh ? `${item.nameEn || item.code} (${item.nameZh})` : (item.nameEn || item.code)) : '';

  // --- Activity table: per-column free-text filter + click-to-sort ---
  const activityColumns = [
    { key: 'id', label: t('ole.activityId') },
    { key: 'title', label: t('ole.title') },
    { key: 'dateTime', label: t('ole.dateTime') },
    { key: 'organization', label: t('ole.organization') },
    { key: 'nature', label: t('ole.nature') },
    { key: 'staffInCharge', label: t('ole.staffInCharge') },
    { key: 'students', label: t('common.students') },
  ];
  const [columnFilters, setColumnFilters] = useState({ id: '', title: '', dateTime: '', organization: '', nature: '', staffInCharge: '', students: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const getColumnValue = (a, key) => {
    switch (key) {
      case 'id': return a.id || '';
      case 'title': return `${a.titleEn || ''} ${a.titleZh || ''}`.trim();
      case 'dateTime': return `${a.activityDate || ''} ${a.timeStart || ''}`.trim();
      case 'organization': return bilingualLabel(organizationMap[a.organization]) || a.organization || '';
      case 'nature': return bilingualLabel(natureMap[a.nature]) || a.nature || '';
      case 'staffInCharge': return a.staffInCharge || '';
      case 'students': return String((a.studentInvolved || []).length);
      default: return '';
    }
  };

  // direction === null clears sort on that column (called from ColumnMenu's popup)
  const setColumnSort = (key, direction) => setSortConfig(direction ? { key, direction } : { key: null, direction: 'asc' });

  const displayActivities = useMemo(() => {
    let list = activities.filter(a => activityColumns.every(({ key }) => {
      const filterVal = columnFilters[key].trim().toLowerCase();
      return !filterVal || getColumnValue(a, key).toLowerCase().includes(filterVal);
    }));
    if (sortConfig.key) {
      list = [...list].sort((a, b) => {
        const va = getColumnValue(a, sortConfig.key);
        const vb = getColumnValue(b, sortConfig.key);
        const cmp = sortConfig.key === 'students' ? (Number(va) - Number(vb)) : va.localeCompare(vb, undefined, { numeric: true });
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, columnFilters, sortConfig, organizationMap, natureMap]);

  // --- Settings save ---
  // A row's code is a "rename" (vs. a brand-new row) if its stable _rowId already existed in the
  // last-loaded snapshot under a different, non-blank code.
  const computeRenames = (items, listKey) => {
    const originalMap = originalCodesRef.current[listKey] || {};
    const renames = [];
    items.forEach(item => {
      const oldCode = originalMap[item._rowId];
      const newCode = (item.code || '').trim();
      if (oldCode && newCode && oldCode !== newCode) renames.push({ from: oldCode, to: newCode });
    });
    return renames;
  };

  // Renames a flat code field (organization/nature/role) across every matching doc in a collection.
  const cascadeCodeRename = async (collectionName, field, oldCode, newCode) => {
    const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, collectionName), where(field, '==', oldCode)));
    if (snap.empty) return 0;
    let batch = writeBatch(db);
    let batchCount = 0;
    for (const docSnap of snap.docs) {
      batch.update(docSnap.ref, { [field]: newCode, updatedAt: new Date().toISOString(), updatedBy: auth.currentUser?.email || 'Unknown' });
      batchCount++;
      if (batchCount === 500) { await batch.commit(); batch = writeBatch(db); batchCount = 0; }
    }
    if (batchCount > 0) await batch.commit();
    return snap.size;
  };

  // participantDetails is a map keyed by SID (not a queryable field), so a role rename needs a full scan
  // of ole_record across all school years rather than a `where` query.
  const cascadeParticipantRoleRename = async (oldCode, newCode) => {
    const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'ole_record'));
    let batch = writeBatch(db);
    let batchCount = 0;
    let updatedDocs = 0;
    for (const docSnap of snap.docs) {
      const details = docSnap.data().participantDetails;
      if (!details || typeof details !== 'object') continue;
      let changed = false;
      const nextDetails = {};
      Object.entries(details).forEach(([sid, info]) => {
        if (info && info.role === oldCode) { nextDetails[sid] = { ...info, role: newCode }; changed = true; }
        else nextDetails[sid] = info;
      });
      if (!changed) continue;
      batch.update(docSnap.ref, { participantDetails: nextDetails, updatedAt: new Date().toISOString(), updatedBy: auth.currentUser?.email || 'Unknown' });
      batchCount++;
      updatedDocs++;
      if (batchCount === 500) { await batch.commit(); batch = writeBatch(db); batchCount = 0; }
    }
    if (batchCount > 0) await batch.commit();
    return updatedDocs;
  };

  const handleSaveSettings = async () => {
    if (!activeSchoolId) return;

    const orgRenames = computeRenames(organizations, 'organizations');
    const natureRenames = computeRenames(natures, 'natures');
    const participantRoleRenames = computeRenames(participantRoles, 'participantRoles');
    const leaderRoleRenames = computeRenames(leaderRoles, 'leaderRoles');
    const allRenames = [...orgRenames, ...natureRenames, ...participantRoleRenames, ...leaderRoleRenames];

    if (allRenames.length > 0) {
      const summary = [
        ...orgRenames.map(r => `Organization: ${r.from} → ${r.to}`),
        ...natureRenames.map(r => `Nature: ${r.from} → ${r.to}`),
        ...participantRoleRenames.map(r => `Participant Role: ${r.from} → ${r.to}`),
        ...leaderRoleRenames.map(r => `Leader Role: ${r.from} → ${r.to}`),
      ].join('\n');
      if (!window.confirm(t('ole.codeChangeConfirm', { summary }))) {
        return;
      }
    }

    setSavingSettings(true);
    try {
      let cascadeCount = 0;
      for (const r of orgRenames) {
        cascadeCount += await cascadeCodeRename('ole_record', 'organization', r.from, r.to);
        cascadeCount += await cascadeCodeRename('leader_record', 'organization', r.from, r.to);
      }
      for (const r of natureRenames) {
        cascadeCount += await cascadeCodeRename('ole_record', 'nature', r.from, r.to);
      }
      for (const r of participantRoleRenames) {
        cascadeCount += await cascadeParticipantRoleRename(r.from, r.to);
      }
      for (const r of leaderRoleRenames) {
        cascadeCount += await cascadeCodeRename('leader_record', 'role', r.from, r.to);
      }

      const stripRowId = ({ _rowId, ...rest }) => rest;
      const cleanOrgs = organizations.filter(o => (o.code || '').trim()).map(stripRowId);
      const cleanNatures = natures.filter(n => (n.code || '').trim()).map(stripRowId);
      const cleanRoles = participantRoles.filter(r => (r.code || '').trim()).map(stripRowId);
      const cleanLeaderRoles = leaderRoles.filter(r => (r.code || '').trim()).map(stripRowId);
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'ole_settings'), {
        organizations: cleanOrgs,
        natures: cleanNatures,
        participantRoles: cleanRoles,
        leaderRoles: cleanLeaderRoles,
      });

      const orgsWithIds = withRowIds(cleanOrgs);
      const naturesWithIds = withRowIds(cleanNatures);
      const rolesWithIds = withRowIds(cleanRoles);
      const leaderRolesWithIds = withRowIds(cleanLeaderRoles);
      setOrganizations(orgsWithIds);
      setNatures(naturesWithIds);
      setParticipantRoles(rolesWithIds);
      setLeaderRoles(leaderRolesWithIds);
      originalCodesRef.current = {
        organizations: buildCodeMap(orgsWithIds),
        natures: buildCodeMap(naturesWithIds),
        participantRoles: buildCodeMap(rolesWithIds),
        leaderRoles: buildCodeMap(leaderRolesWithIds),
      };

      if (allRenames.length > 0) {
        await fetchActivities();
        await fetchLeaders();
        alert(t('ole.settingsSavedCascade', { count: cascadeCount }));
      } else {
        alert(t('ole.settingsSaved'));
      }
    } catch (e) {
      console.error('Error saving OLE settings', e);
      alert(t('ole.settingsSaveFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // Bulk-copy Department/Committee system parameters into Organizations, skipping codes already present.
  // Codes and names can still be freely edited afterward before saving.
  const handleImportOrganizations = () => {
    const existingCodes = new Set(organizations.map(o => o.code));
    const newCandidates = importCandidates.filter(c => !existingCodes.has(c.code));
    if (newCandidates.length === 0) {
      alert(t('ole.nothingToImport'));
      return;
    }
    let nextSeq = organizations.reduce((m, o) => Math.max(m, Number(o.seq) || 0), 0);
    const newRows = newCandidates.map(c => {
      nextSeq += 10;
      return { ...c, seq: nextSeq, teacherInCharge: '', _rowId: generateRowId() };
    });
    setOrganizations([...organizations, ...newRows]);
  };

  // --- Activity CRUD ---
  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  // Turns the list of dates into ole_session rows.
  //
  // Materialised rather than recomputed on read, because a date list is an INTENTION and a session is
  // a FACT: a typhoon signal, an exam clash or a venue clash makes one day diverge from the plan, and
  // once the sessions exist each one can be cancelled or moved without touching the others.
  //
  // Ids are deterministic (activity + date), so saving twice replaces rather than duplicates. Days
  // dropped from the list have their sessions deleted -- but the picker will not let a day with
  // attendance be dropped in the first place.
  const syncSessions = async (activityId, payload) => {
    const wanted = payload.sessionDates || [];
    try {
      const existing = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'ole_session'),
        where('activityId', '==', activityId),
      ));

      const batch = writeBatch(db);
      const keep = new Set();

      wanted.forEach((date) => {
        const id = `${activityId}__${date}`;
        keep.add(id);
        batch.set(doc(db, 'schools', activeSchoolId, 'ole_session', id), {
          activityId,
          date,
          timeStart: payload.timeStart || '',
          timeEnd: payload.timeEnd || '',
          venueEn: payload.venueEn || '',
          venueZh: payload.venueZh || '',
          titleEn: payload.titleEn || '',
          titleZh: payload.titleZh || '',
          schoolYear: payload.schoolYear || '',
          status: 'scheduled',
          ...ownerStamp(),
        }, { merge: true });
      });

      existing.forEach((d) => {
        if (!keep.has(d.id)) batch.delete(d.ref);
      });

      await batch.commit();
    } catch (e) {
      console.error('Could not sync sessions', e);
      alert(t('ole.sessionsSyncFailed', { message: e.message }));
    }
  };

  // Duplicates an activity so a similar one does not have to be typed out again.
  //
  // What is NOT copied is the point of the design: attendance, reply-slip signatures, sessions and
  // the author all belong to the run that already happened. Dates are cleared too -- you copy an
  // activity in order to hold it again on different days, so carrying the old ones over would just
  // be something to remember to delete.
  //
  // Participants and their roles are separate choices because "the same students, but roles
  // reassigned" is the common case, not an edge one.
  // Issues one notice covering the whole activity.
  //
  // One notice for all its days is the point: a five-day activity used to be five activities and
  // therefore five identical slips. The dates travel with the notice so a family signing once can
  // see exactly what they are agreeing to -- a slip covering five days without naming them is signed
  // blind.
  //
  // Uses src/noticeFanOut.js rather than writing its own rows. That code has already had both a
  // roster leak and a timeout; a second copy of it living in this module is how the platform ended
  // up with four separate "authorised staff" lists.
  const handleIssueNotice = async () => {
    if (!noticeSource) return;
    const sids = Array.isArray(noticeSource.studentInvolved) ? noticeSource.studentInvolved : [];
    if (sids.length === 0) { alert(t('ole.noParticipants')); return; }
    if (!noticeContent.trim()) { alert(t('ole.writeNoticeFirst')); return; }

    setIssuingNotice(true);
    try {
      const nid = `PN-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 10000)}`;
      const now = new Date().toISOString();
      const issuer = staffList.find(st => st.initial === noticeIssuer);
      const title = noticeSource.titleEn || noticeSource.titleZh || noticeSource.activityId;
      const dates = [...(noticeSource.sessionDates || [])].sort();

      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_notice', nid), {
        noticeId: nid,
        title,
        content: noticeContent.trim(),
        eventDates: dates,
        // Which activity this came from, so the two are not merely similar-looking records.
        activityId: noticeSource.id,
        issuerEmail: issuer?.email || '',
        issuerId: issuer?.initial || '',
        issuerName: issuer?.name || '',
        issuedByEmail: (auth.currentUser?.email || '').toLowerCase(),
        issue_datetime: now,
        targetSids: sids,
        targetCount: sids.length,
        audienceEmails: audienceFor(studentMap, sids),
        attachmentUrl: '', attachmentName: '', attachmentPath: '',
        // A notice raised here is a submission, not a letter. It goes through the same two approval
        // stages as one drafted in Parent Notice -- an activity organiser writing to every parent on
        // their own say-so is exactly what the approval step exists to prevent.
        status: firstPendingStatus(),
        approvalLog: [logEntry({
          action: 'submit',
          byEmail: (auth.currentUser?.email || '').toLowerCase(),
          note: 'Raised from OLE Management.',
        })],
      });

      // No fan-out here on purpose: the recipient rows are written at final approval, on the Notices
      // tab. Until then the notice has no recipients at all, so there is no window in which an
      // unapproved letter is sitting where a parent could read it.

      setNoticeSource(null);
      setNoticeContent('');
      alert(t('ole.noticeSubmitted', { id: nid, count: sids.length }));
    } catch (e) {
      console.error(e);
      alert(t('ole.noticeFailed', { message: e.message }));
    }
    setIssuingNotice(false);
  };

  const handleCopyActivity = async () => {
    if (!copySource) return;
    setCopying(true);
    try {
      const src = copySource;
      const newId = await generateActivityId(schoolYear);

      const payload = {
        activityId: newId,
        schoolYear,
        titleEn: src.titleEn || '',
        titleZh: src.titleZh || '',
        descriptionEn: src.descriptionEn || '',
        descriptionZh: src.descriptionZh || '',
        organization: src.organization || '',
        nature: src.nature || '',
        venueEn: src.venueEn || '',
        venueZh: src.venueZh || '',
        timeStart: src.timeStart || '',
        timeEnd: src.timeEnd || '',
        staffInCharge: src.staffInCharge || '',
        accompanyStaff: Array.isArray(src.accompanyStaff) ? src.accompanyStaff : [],
        replyToStaff: src.replyToStaff || '',
        // Deliberately blank: dates, reply deadline and the student list all belong to the run being
        // copied from.
        replyDueDate: '',
        activityDate: '',
        sessionDates: [],
        studentInvolved: copyParticipants ? [...(src.studentInvolved || [])] : [],
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.email || 'Unknown',
        ...ownerStamp(),
      };

      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_record', newId), payload);

      if (copyParticipants) {
        const parts = await getDocs(query(
          collection(db, 'schools', activeSchoolId, 'ole_participant'),
          where('activityId', '==', src.id),
        ));
        let batch = writeBatch(db);
        let pending = 0;
        parts.forEach((d) => {
          const v = d.data() || {};
          const ref = doc(collection(db, 'schools', activeSchoolId, 'ole_participant'));
          batch.set(ref, {
            ...v,
            activityId: newId,
            titleEn: payload.titleEn,
            titleZh: payload.titleZh,
            // The new run has not happened, so nobody has a role in it unless roles were copied, and
            // nobody has attended it at all.
            role: copyRoles ? (v.role || '') : '',
            attendance: 'pending',
            achievement: '',
            activityDate: '',
            ...ownerStamp(),
          });
          pending += 1;
          if (pending >= 400) { batch.commit(); batch = writeBatch(db); pending = 0; }
        });
        if (pending > 0) await batch.commit();
      }

      setCopySource(null);
      await fetchActivities();
      alert(t('ole.copiedTo', { id: newId }));
    } catch (e) {
      console.error(e);
      alert(t('ole.copyFailed', { message: e.message }));
    }
    setCopying(false);
  };

  const openEditModal = async (activity) => {
    setEditingId(activity.id);
    // Which of this activity's days somebody has already been marked for. Removing such a day would
    // leave attendance rows pointing at a session that no longer exists.
    setLockedSessionDates([]);
    try {
      const attSnap = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'ole_attendance'),
        where('activityId', '==', activity.id),
      ));
      const marked = new Set();
      attSnap.forEach((d) => {
        const v = d.data() || {};
        if (v.date && v.status && v.status !== 'pending') marked.add(v.date);
      });
      setLockedSessionDates([...marked]);
    } catch (e) {
      console.error('Could not check which dates already have attendance', e);
    }
    setForm({
      titleEn: activity.titleEn || '',
      titleZh: activity.titleZh || '',
      descriptionEn: activity.descriptionEn || '',
      descriptionZh: activity.descriptionZh || '',
      organization: activity.organization || '',
      nature: activity.nature || '',
      activityDate: activity.activityDate || '',
      sessionDates: Array.isArray(activity.sessionDates) && activity.sessionDates.length
        ? [...activity.sessionDates].sort()
        : (activity.activityDate ? [activity.activityDate] : []),
      timeStart: activity.timeStart || '',
      timeEnd: activity.timeEnd || '',
      venueEn: activity.venueEn || '',
      venueZh: activity.venueZh || '',
      staffInCharge: activity.staffInCharge || '',
      accompanyStaff: Array.isArray(activity.accompanyStaff) ? activity.accompanyStaff : [],
      replyToStaff: activity.replyToStaff || '',
      replyDueDate: activity.replyDueDate || '',
    });
    setShowModal(true);
  };

  const generateActivityId = async (yr) => {
    const counterRef = doc(db, 'schools', activeSchoolId, 'metadata', 'ole_counters');
    let newId = '';
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const data = snap.exists() ? snap.data() : {};
      const next = (data[yr] || 0) + 1;
      newId = `${yr.replace('-', '')}-${String(next).padStart(6, '0')}`;
      tx.set(counterRef, { ...data, [yr]: next }, { merge: true });
    });
    return newId;
  };

  const handleSaveActivity = async (e) => {
    e.preventDefault();
    if (!form.titleEn.trim() && !form.titleZh.trim()) return alert(t('ole.titleRequired'));
    if (!schoolYear) return alert(t('ole.selectYearFirst'));
    setSaving(true);
    try {
      let activityId = editingId;
      const isNew = !activityId;
      if (isNew) {
        activityId = await generateActivityId(schoolYear);
      }
      const payload = {
        activityId,
        schoolYear,
        titleEn: form.titleEn.trim(),
        titleZh: form.titleZh.trim(),
        descriptionEn: form.descriptionEn.trim(),
        descriptionZh: form.descriptionZh.trim(),
        organization: form.organization,
        nature: form.nature,
        // First date, derived. Kept so the existing lists, sorts and CSV export need no changes.
        activityDate: (form.sessionDates || [])[0] || form.activityDate || '',
        sessionDates: [...new Set(form.sessionDates || [])].sort(),
        timeStart: form.timeStart,
        timeEnd: form.timeEnd,
        venueEn: form.venueEn.trim(),
        venueZh: form.venueZh.trim(),
        staffInCharge: form.staffInCharge,
        accompanyStaff: form.accompanyStaff,
        replyToStaff: form.replyToStaff,
        replyDueDate: form.replyDueDate,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      };
      if (isNew) {
        payload.studentInvolved = [];
        payload.createdAt = new Date().toISOString();
        payload.createdBy = auth.currentUser?.email || 'Unknown';
        Object.assign(payload, ownerStamp());
      }
      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_record', activityId), payload, { merge: true });
      await syncSessions(activityId, payload);
      setShowModal(false);
      setForm(emptyForm());
      setEditingId(null);
      const list = await fetchActivities();
      if (isNew) {
        const created = (list || []).find(a => a.id === activityId) || { id: activityId, ...payload, studentInvolved: [] };
        openParticipantModal(created);
      }
    } catch (e2) {
      console.error('Error saving activity', e2);
      alert(t('ole.saveActivityFailed', { message: e2.message }));
    }
    setSaving(false);
  };

  const handleDeleteActivity = async (activity) => {
    const rowIds = await allParticipantRowIds(activity.id).catch(() => []);
    const n = rowIds.length;
    if (!window.confirm(
      t('ole.deleteActivityConfirm', { title: activity.titleEn || activity.titleZh, id: activity.id })
      + (n ? t('ole.deleteActivityPortfolios', { count: n }) : '')
      + t('ole.cannotBeUndone'),
    )) return;
    try {
      // The rows go FIRST. If the activity document were removed first and this then failed, the
      // rows would be orphaned with nothing left on the staff side to find them from -- which is
      // precisely the state this is fixing.
      const chunk = 400;
      for (let i = 0; i < rowIds.length; i += chunk) {
        const batch = writeBatch(db);
        rowIds.slice(i, i + chunk).forEach(id => {
          batch.delete(doc(db, 'schools', activeSchoolId, 'ole_participant', id));
        });
        await batch.commit();
      }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'ole_record', activity.id));
      fetchActivities();
    } catch (e) {
      console.error('Error deleting activity', e);
      alert(t('ole.deleteActivityFailed', { message: e.message }));
    }
  };

  // --- CSV batch export / import (papaparse, matching the pattern already used in Assessment.jsx) ---
  const handleExportCSV = () => {
    const csvData = activities.map(a => {
      const row = {};
      CSV_COLUMNS.forEach(col => {
        row[col] = (col === 'accompanyStaff' || col === 'studentInvolved') ? (a[col] || []).join(';') : (a[col] ?? '');
      });
      return row;
    });
    if (csvData.length === 0) csvData.push(Object.fromEntries(CSV_COLUMNS.map(c => [c, ''])));
    const csv = Papa.unparse(csvData, { columns: CSV_COLUMNS });
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ole_activities_${schoolYear || 'export'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Batch add/update: rows with a matching activityId update that record (merge), blank activityId creates a new one.
  // Existence is checked directly against Firestore (not the locally-loaded `activities` list) since an imported
  // row's schoolYear column can target a year other than the one currently selected in the tabs bar.
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeSchoolId) return;
    setImporting(true);
    try {
      const results = await new Promise((resolve, reject) => {
        Papa.parse(file, { header: true, skipEmptyLines: true, complete: resolve, error: reject });
      });
      const dataRows = results.data;
      if (dataRows.length === 0) { alert(t('ole.csvNoRows')); setImporting(false); return; }

      let added = 0, updated = 0, skipped = 0;
      // Writes go through a chunked writeBatch (500 ops/commit, matching Assessment.jsx's import) so a large
      // CSV sends a handful of network round-trips instead of one per row. This doesn't reduce Firestore's
      // billed write count (each doc write is still billed individually) — it cuts round-trip latency, which
      // is what made large imports slow. ID generation and the existence-check read stay per-row (unbatched);
      // that's a separate optimization (reserving a block of IDs up front instead of one runTransaction per row).
      let batch = writeBatch(db);
      let batchCount = 0;
      const commitBatch = async () => {
        if (batchCount === 0) return;
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      };

      for (const raw of dataRows) {
        const rec = Object.fromEntries(Object.entries(raw).map(([k, v]) => [(k || '').trim(), (v ?? '').trim()]));
        const yr = rec.schoolYear || schoolYear;
        if ((!rec.titleEn && !rec.titleZh) || !yr) { skipped++; continue; }

        let activityId = rec.activityId || '';
        const isNew = !activityId;
        if (isNew) activityId = await generateActivityId(yr);

        const docRef = doc(db, 'schools', activeSchoolId, 'ole_record', activityId);
        const existingSnap = isNew ? null : await getDoc(docRef);
        const isCreate = isNew || !existingSnap.exists();

        const payload = {
          activityId,
          schoolYear: yr,
          titleEn: rec.titleEn || '',
          titleZh: rec.titleZh || '',
          descriptionEn: rec.descriptionEn || '',
          descriptionZh: rec.descriptionZh || '',
          organization: rec.organization || '',
          nature: rec.nature || '',
          activityDate: rec.activityDate || '',
          timeStart: rec.timeStart || '',
          timeEnd: rec.timeEnd || '',
          venueEn: rec.venueEn || '',
          venueZh: rec.venueZh || '',
          staffInCharge: rec.staffInCharge || '',
          accompanyStaff: rec.accompanyStaff ? rec.accompanyStaff.split(';').map(s => s.trim()).filter(Boolean) : [],
          replyToStaff: rec.replyToStaff || '',
          replyDueDate: rec.replyDueDate || '',
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email || 'Unknown',
        };
        if (rec.studentInvolved) {
          payload.studentInvolved = rec.studentInvolved.split(';').map(s => s.trim()).filter(Boolean);
        } else if (isCreate) {
          payload.studentInvolved = [];
        }
        if (isCreate) {
          payload.createdAt = new Date().toISOString();
          payload.createdBy = auth.currentUser?.email || 'Unknown';
          Object.assign(payload, ownerStamp());
        }

        batch.set(docRef, payload, { merge: true });
        batchCount++;
        if (isCreate) added++; else updated++;
        if (batchCount === 500) await commitBatch();
      }
      await commitBatch();
      await fetchActivities();
      // Whole sentences joined, not one built from a count and a conditional clause.
      alert([
        t('ole.imported', { added, updated }),
        ...(skipped ? [t('ole.importedSkipped', { count: skipped })] : []),
      ].join(' '));
    } catch (err) {
      console.error('Error importing CSV', err);
      alert(t('ole.importFailed', { message: err.message }));
    }
    setImporting(false);
  };

  // --- Participant management (separate step, per the requested two-stage flow) ---
  const openParticipantModal = (activity) => {
    setParticipantActivity(activity);
    setParticipantSelected(Array.isArray(activity.studentInvolved) ? activity.studentInvolved : []);
    setParticipantSearch('');
    setParticipantClassFilter('');
    setShowParticipantModal(true);
  };

  const toggleParticipant = (sid) => {
    setParticipantSelected(prev => prev.includes(sid) ? prev.filter(s => s !== sid) : [...prev, sid]);
  };

  const filteredParticipantStudents = useMemo(() => {
    const txt = participantSearch.trim().toLowerCase();
    return studentList.filter(s => {
      const matchCls = !participantClassFilter || s.currentClass === participantClassFilter;
      const matchTxt = !txt || s.name.toLowerCase().includes(txt) || s.chineseName.includes(participantSearch) || s.sid.toLowerCase().includes(txt);
      return matchCls && matchTxt;
    });
  }, [studentList, participantSearch, participantClassFilter]);

  // "Select All" toggles every currently-filtered student (e.g. a whole class filtered via the dropdown),
  // not the entire student body, so batch-adding a class doesn't silently touch students outside the filter.
  const allFilteredSelected = filteredParticipantStudents.length > 0 && filteredParticipantStudents.every(s => participantSelected.includes(s.sid));
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (!selectAllRef.current) return;
    const selectedCount = filteredParticipantStudents.filter(s => participantSelected.includes(s.sid)).length;
    selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < filteredParticipantStudents.length;
  }, [filteredParticipantStudents, participantSelected]);

  const toggleSelectAllFiltered = () => {
    setParticipantSelected(prev => {
      if (allFilteredSelected) {
        const filteredSids = new Set(filteredParticipantStudents.map(s => s.sid));
        return prev.filter(sid => !filteredSids.has(sid));
      }
      const merged = new Set(prev);
      filteredParticipantStudents.forEach(s => merged.add(s.sid));
      return [...merged];
    });
  };

  const handleSaveParticipants = async () => {
    if (!participantActivity) return;
    setSavingParticipants(true);
    try {
      const before = Array.isArray(participantActivity.studentInvolved) ? participantActivity.studentInvolved : [];
      const now = new Set(participantSelected);
      const removed = before.filter(sid => !now.has(sid));

      // participantDetails is a MAP keyed by SID, and `merge: true` merges maps key by key: it can
      // add a key and change one, but it can never remove one. Leaving the key behind is not merely
      // untidy -- put the student back on the activity later and last year's role and achievement
      // come back with them, silently, and get fanned out to their row as if they were entered
      // today. deleteField() is the only way to actually drop it.
      const detailDeletions = {};
      removed.forEach(sid => { detailDeletions[sid] = deleteField(); });

      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_record', participantActivity.id), {
        studentInvolved: participantSelected,
        ...emailsForSids(participantSelected),
        ...(removed.length ? { participantDetails: detailDeletions } : {}),
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      }, { merge: true });

      await fanOutParticipants(
        { ...participantActivity, id: participantActivity.id },
        participantSelected,
      );
      // A row is what the family reads. Writing rows for who is on the activity was never enough:
      // without this, somebody taken off it kept seeing it in their portfolio for good.
      if (removed.length) await removeParticipantRows(participantActivity.id, removed);
      setShowParticipantModal(false);
      setParticipantActivity(null);
      fetchActivities();
    } catch (e) {
      console.error('Error saving participants', e);
      alert(t('ole.saveParticipantsFailed', { message: e.message }));
    }
    setSavingParticipants(false);
  };

  // --- Participant Role modal: per-participant role + achievement, keyed by SID, stored on the activity doc ---
  // Opened from an action button on the Activity list row (same pattern as openParticipantModal), not a separate tab.
  const ATTENDANCE_STATES = [
    { id: 'present', label: t('ole.attendance.present'), icon: Check, colour: 'var(--color-success)' },
    { id: 'absent', label: t('ole.attendance.absent'), icon: X, colour: 'var(--color-danger)' },
    { id: 'pending', label: t('ole.attendance.pending'), icon: HelpCircle, colour: 'var(--color-warning)' },
  ];

  // This started life as a document-submission tracker, so anything recorded before the rename is
  // stored under docSubmission with submitted/not_submitted values. Read both and translate.
  const LEGACY_ATTENDANCE = { submitted: 'present', not_submitted: 'absent' };

  const openAttendanceModal = async (activity) => {
    setAttendanceActivity(activity);
    setAttendanceSessions([]);
    setAttendanceMap({});
    setShowAttendanceModal(true);

    try {
      const snap = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'ole_session'),
        where('activityId', '==', activity.id),
      ));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setAttendanceSessions(list);

      // Land on today if the activity runs today, otherwise the first day. Staff take attendance on
      // the day it happens, so making them pick from a list every time would be friction for the
      // common case.
      const today = new Date().toISOString().slice(0, 10);
      const pick = list.find(x => x.date === today) || list[0];
      if (pick) await loadSessionRegister(activity, pick.id);
    } catch (e) {
      console.error('Could not load sessions', e);
    }
  };

  // The register for one session. Anyone without a recorded answer starts as pending rather than
  // blank, so the counts always add up to the number of participants.
  const loadSessionRegister = async (activity, sessionId) => {
    setAttendanceSessionId(sessionId);
    const sids = Array.isArray(activity.studentInvolved) ? activity.studentInvolved : [];
    try {
      const snap = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'ole_attendance'),
        where('sessionId', '==', sessionId),
      ));
      const bySid = {};
      snap.forEach(d => {
        const v = d.data() || {};
        if (v.sid) bySid[v.sid] = LEGACY_ATTENDANCE[v.status] || v.status || 'pending';
      });
      setAttendanceMap(Object.fromEntries(sids.map(sid => [sid, bySid[sid] || 'pending'])));
    } catch (e) {
      console.error('Could not load the register', e);
      setAttendanceMap(Object.fromEntries(sids.map(sid => [sid, 'pending'])));
    }
  };

  const handleSaveAttendance = async () => {
    if (!attendanceActivity || !attendanceSessionId) return;
    setSavingAttendance(true);
    try {
      const session = attendanceSessions.find(x => x.id === attendanceSessionId) || {};

      // One row per (session, student), never a map keyed by student on a shared document. That was
      // participantDetails, which rewrote the whole dictionary on every save and erased a set of
      // attendance once already -- and a shared map cannot be scoped to one family by any rule.
      let batch = writeBatch(db);
      let pending = 0;
      for (const sid of Object.keys(attendanceMap)) {
        const st = studentMap[sid] || {};
        const ref = doc(db, 'schools', activeSchoolId, 'ole_attendance',
                        `${attendanceSessionId}__${sid}`);
        batch.set(ref, {
          sessionId: attendanceSessionId,
          activityId: attendanceActivity.id,
          date: session.date || '',
          timeStart: session.timeStart || '',
          timeEnd: session.timeEnd || '',
          sid,
          studentEmail: st.email || '',
          guardianEmails: st.guardianEmails || [],
          studentName: st.name || '',
          className: st.className || '',
          status: attendanceMap[sid] || 'pending',
          titleEn: attendanceActivity.titleEn || '',
          titleZh: attendanceActivity.titleZh || '',
          schoolYear: attendanceActivity.schoolYear || '',
          recordedByEmail: (auth.currentUser?.email || '').toLowerCase(),
          recordedAt: new Date().toISOString(),
        }, { merge: true });
        pending += 1;
        if (pending >= 400) { await batch.commit(); batch = writeBatch(db); pending = 0; }
      }
      if (pending > 0) await batch.commit();

      setShowAttendanceModal(false);
    } catch (e) {
      console.error('Error saving attendance', e);
      alert(t('ole.saveFailed', { message: e.message }));
    }
    setSavingAttendance(false);
  };

  const openRoleModal = (activity) => {
    const details = activity.participantDetails || {};
    const sids = Array.isArray(activity.studentInvolved) ? activity.studentInvolved : [];
    // Spread the existing detail first so any field other than role/achievement survives a save --
    // this modal writes participantDetails back in full, so anything it fails to carry is lost.
    setRoleAssignments(Object.fromEntries(sids.map(sid => [sid, {
      ...(details[sid] || {}),
      role: details[sid]?.role || '',
      achievement: details[sid]?.achievement || '',
    }])));
    setRoleActivity(activity);
    setShowRoleModal(true);
  };

  const updateRoleAssignment = (sid, key, val) => {
    setRoleAssignments(prev => ({ ...prev, [sid]: { ...prev[sid], [key]: val } }));
  };

  const handleSaveRoleAssignments = async () => {
    if (!roleActivity) return;
    setSavingRoleAssignments(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_record', roleActivity.id), {
        // This comment used to say that merge:true only merges top-level fields and replaces a
        // nested map outright. That is the opposite of what Firestore does, and believing it is how
        // three separate faults got written: a merge walks INTO a map key by key, so it can add and
        // change but never remove. Verified against the emulator in tests/persistence.test.js.
        //
        // The fold below is still right, for the real reason: it carries any key this modal does
        // not know about. Removal is handled where removal happens -- handleSaveParticipants --
        // using deleteField(), because a fold like this one can never express it.
        participantDetails: { ...(roleActivity.participantDetails || {}), ...roleAssignments },
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      }, { merge: true });
      await fanOutParticipants(roleActivity, Object.keys(roleAssignments), { roles: roleAssignments });
      await fetchActivities();
      setShowRoleModal(false);
      setRoleActivity(null);
    } catch (e) {
      console.error('Error saving participant roles', e);
      alert(t('ole.saveRolesFailed', { message: e.message }));
    }
    setSavingRoleAssignments(false);
  };

  // --- Portfolio tab: a student's full OLE history across all school years, grouped by year desc / date desc ---
  const portfolioClassStudents = useMemo(() => studentList.filter(s => s.currentClass === portfolioClass), [studentList, portfolioClass]);

  useEffect(() => {
    if (!activeSchoolId || !portfolioStudentSid) { setPortfolioActivities([]); return; }
    setLoadingPortfolio(true);
    setExpandedPortfolioIds(new Set());
    const q = query(collection(db, 'schools', activeSchoolId, 'ole_record'), where('studentInvolved', 'array-contains', portfolioStudentSid));
    getDocs(q)
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        setPortfolioActivities(list);
      })
      .catch(e => { console.error('Error loading portfolio', e); setPortfolioActivities([]); })
      .finally(() => setLoadingPortfolio(false));
  }, [activeSchoolId, portfolioStudentSid]);

  const portfolioGroups = useMemo(() => {
    const groups = {};
    portfolioActivities.forEach(a => {
      const yr = a.schoolYear || 'Unknown';
      (groups[yr] = groups[yr] || []).push(a);
    });
    return Object.keys(groups)
      .sort((a, b) => b.localeCompare(a))
      .map(yr => ({
        schoolYear: yr,
        items: [...groups[yr]].sort((a, b) => `${b.activityDate || ''} ${b.timeStart || ''}`.localeCompare(`${a.activityDate || ''} ${a.timeStart || ''}`)),
      }));
  }, [portfolioActivities]);

  const togglePortfolioExpand = (id) => {
    setExpandedPortfolioIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // --- Leaders tab: student union / prefect / house captain etc. roster for the selected school year ---
  const fetchLeaders = async () => {
    if (!activeSchoolId || !schoolYear) { setLeaderRecords([]); return; }
    setLoadingLeaders(true);
    try {
      const q = query(collection(db, 'schools', activeSchoolId, 'leader_record'), where('schoolYear', '==', schoolYear));
      const snap = await getDocs(q);
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setLeaderRecords(list);
    } catch (e) {
      console.error('Error loading leader records', e);
    } finally {
      setLoadingLeaders(false);
    }
  };

  useEffect(() => {
    fetchLeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId, schoolYear]);

  const leaderClassStudents = useMemo(() => studentList.filter(s => s.currentClass === leaderClassFilter), [studentList, leaderClassFilter]);

  const openAddLeaderModal = () => {
    setEditingLeaderId(null);
    setLeaderForm({ organization: '', role: '', sid: '' });
    setLeaderClassFilter('');
    setShowLeaderModal(true);
  };

  const openEditLeaderModal = (rec) => {
    setEditingLeaderId(rec.id);
    setLeaderForm({ organization: rec.organization || '', role: rec.role || '', sid: rec.sid || '' });
    setLeaderClassFilter(studentMap[rec.sid]?.currentClass || '');
    setShowLeaderModal(true);
  };

  const handleSaveLeader = async (e) => {
    e.preventDefault();
    if (!leaderForm.organization || !leaderForm.role || !leaderForm.sid) return alert(t('ole.leaderFieldsRequired'));
    if (!schoolYear) return alert(t('ole.selectYearFirst'));
    setSavingLeader(true);
    try {
      const payload = {
        schoolYear,
        organization: leaderForm.organization,
        role: leaderForm.role,
        sid: leaderForm.sid,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      };
      if (editingLeaderId) {
        await setDoc(doc(db, 'schools', activeSchoolId, 'leader_record', editingLeaderId), payload, { merge: true });
      } else {
        payload.createdAt = new Date().toISOString();
        payload.createdBy = auth.currentUser?.email || 'Unknown';
        Object.assign(payload, ownerStamp());
        await setDoc(doc(collection(db, 'schools', activeSchoolId, 'leader_record')), { ...payload, ...ownerStamp() });
      }
      setShowLeaderModal(false);
      fetchLeaders();
    } catch (e2) {
      console.error('Error saving leader record', e2);
      alert(t('ole.saveLeaderFailed', { message: e2.message }));
    }
    setSavingLeader(false);
  };

  const handleDeleteLeader = async (rec) => {
    const st = studentMap[rec.sid];
    const roleLabel = bilingualLabel(leaderRoleMap[rec.role]) || rec.role;
    const orgLabel = bilingualLabel(organizationMap[rec.organization]) || rec.organization;
    if (!window.confirm(t('ole.removeLeaderConfirm', {
      student: st ? `${st.name} ${st.chineseName}`.trim() : rec.sid,
      role: roleLabel,
      organization: orgLabel,
    }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'leader_record', rec.id));
      fetchLeaders();
    } catch (e) {
      console.error('Error deleting leader record', e);
      alert(t('ole.deleteLeaderFailed', { message: e.message }));
    }
  };

  const leaderColumns = [
    { key: 'organization', label: t('ole.organization') },
    { key: 'role', label: t('ole.role') },
    { key: 'student', label: t('common.student') },
    { key: 'class', label: t('common.className') },
  ];
  const [leaderColumnFilters, setLeaderColumnFilters] = useState({ organization: '', role: '', student: '', class: '' });
  const [leaderSortConfig, setLeaderSortConfig] = useState({ key: null, direction: 'asc' });

  const getLeaderColumnValue = (r, key) => {
    const st = studentMap[r.sid];
    switch (key) {
      case 'organization': return bilingualLabel(organizationMap[r.organization]) || r.organization || '';
      case 'role': return bilingualLabel(leaderRoleMap[r.role]) || r.role || '';
      case 'student': return st ? `${st.name} ${st.chineseName}` : r.sid;
      case 'class': return st ? `${st.currentClass} (${st.classNumber})` : '';
      default: return '';
    }
  };

  const setLeaderColumnSort = (key, direction) => setLeaderSortConfig(direction ? { key, direction } : { key: null, direction: 'asc' });

  const displayLeaders = useMemo(() => {
    let list = leaderRecords.filter(r => leaderColumns.every(({ key }) => {
      const filterVal = leaderColumnFilters[key].trim().toLowerCase();
      return !filterVal || getLeaderColumnValue(r, key).toLowerCase().includes(filterVal);
    }));
    if (leaderSortConfig.key) {
      list = [...list].sort((a, b) => {
        const va = getLeaderColumnValue(a, leaderSortConfig.key);
        const vb = getLeaderColumnValue(b, leaderSortConfig.key);
        const cmp = va.localeCompare(vb, undefined, { numeric: true });
        return leaderSortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderRecords, leaderColumnFilters, leaderSortConfig, organizationMap, leaderRoleMap, studentMap]);

  // --- Templates: docx files staff author in Word, uploaded here for storage/preview (not year-scoped) ---
  const fetchTemplates = async () => {
    if (!activeSchoolId) { setTemplates([]); return; }
    setLoadingTemplates(true);
    try {
      const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'ole_templates'));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTemplates(list);
    } catch (e) {
      console.error('Error loading templates', e);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId]);

  const openCreateTemplateModal = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', description: '', file: null });
    setShowTemplateModal(true);
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate(template);
    setTemplateForm({ name: template.name || '', description: template.description || '', file: null });
    setShowTemplateModal(true);
  };

  const openPrintModal = (template) => {
    setPrintTemplate(template);
    setPrintActivityId('');
    setPrintParticipantSid('');
    setMergedBlob(null);
    setTemplatePreviewHtml('');
    setPrintError('');
    setShowPrintModal(true);
  };

  // Row-action entry point from the Activity table: the activity is already known, so pre-select it
  // (and default to every participant, since generating one activity's letters for its whole roster is
  // the common case) and leave Template for the user to pick inside the modal instead.
  const openPrintModalForActivity = (activity) => {
    setPrintTemplate(null);
    setPrintActivityId(activity.id);
    setPrintParticipantSid(ALL_PARTICIPANTS_VALUE);
    setMergedBlob(null);
    setTemplatePreviewHtml('');
    setPrintError('');
    setShowPrintModal(true);
  };

  const printActivity = useMemo(() => activities.find(a => a.id === printActivityId) || null, [activities, printActivityId]);
  const printParticipants = useMemo(() => {
    if (!printActivity) return [];
    return (Array.isArray(printActivity.studentInvolved) ? printActivity.studentInvolved : [])
      .map(sid => studentMap[sid])
      .filter(Boolean);
  }, [printActivity, studentMap]);

  // The sids this Print session should produce a document for: a single participant, activity-level
  // only (empty string -> null), or every participant of the activity when "All Participants" is picked.
  const resolvePrintSids = () => {
    if (printParticipantSid === ALL_PARTICIPANTS_VALUE) return printParticipants.map(s => s.sid);
    return [printParticipantSid || null];
  };

  // Resolves every {tag} this module supports (see TEMPLATE_VARIABLES) to a real value for one
  // activity + optional participant. Missing/inapplicable fields resolve to '' rather than being left
  // as the literal tag, since a half-filled letter reads better than stray curly braces.
  const buildMergeValues = (activity, sid) => {
    const student = sid ? studentMap[sid] : null;
    const detail = sid ? (activity.participantDetails?.[sid] || {}) : {};
    const organization = organizationMap[activity.organization];
    const nature = natureMap[activity.nature];
    const role = roleMap[detail.role];
    return {
      activityTitleEn: activity.titleEn || '',
      activityTitleZh: activity.titleZh || '',
      activityDescriptionEn: activity.descriptionEn || '',
      activityDescriptionZh: activity.descriptionZh || '',
      organizationEn: organization?.nameEn || activity.organization || '',
      organizationZh: organization?.nameZh || '',
      natureEn: nature?.nameEn || activity.nature || '',
      natureZh: nature?.nameZh || '',
      activityDate: activity.activityDate || '',
      timeStart: activity.timeStart || '',
      timeEnd: activity.timeEnd || '',
      venueEn: activity.venueEn || '',
      venueZh: activity.venueZh || '',
      staffInCharge: staffMap[activity.staffInCharge]?.name || activity.staffInCharge || '',
      staffInChargeZh: staffMap[activity.staffInCharge]?.chineseName || '',
      replyToStaff: staffMap[activity.replyToStaff]?.name || activity.replyToStaff || '',
      replyToStaffZh: staffMap[activity.replyToStaff]?.chineseName || '',
      replyDueDate: activity.replyDueDate || '',
      schoolYear: activity.schoolYear || '',
      studentName: student?.name || '',
      studentChineseName: student?.chineseName || '',
      sid: sid || '',
      currentClass: student?.currentClass || '',
      classNumber: student?.classNumber || '',
      roleEn: role?.nameEn || detail.role || '',
      roleZh: role?.nameZh || '',
      achievement: detail.achievement || '',
    };
  };

  // Request/response via a Firestore doc + a Firestore-triggered function (processTemplateFileRequest),
  // not a direct HTTP call -- this org's IAM policy blocks granting invoker access to any new HTTP
  // endpoint, even Firebase Hosting's own automatic grant for itself, so no HTTP-triggered function is
  // reachable at all here. A Firestore trigger needs no invoker permission.
  const fetchTemplateBytes = async (templateId) => {
    const requestRef = doc(collection(db, 'schools', activeSchoolId, 'ole_template_file_requests'));
    await setDoc(requestRef, {
      templateId,
      requestedBy: auth.currentUser?.email || 'Unknown',
      requestedAt: new Date().toISOString(),
      status: 'PENDING',
    });

    const result = await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Timed out waiting for the template file. Please try again.'));
      }, 20000);
      const unsubscribe = onSnapshot(requestRef, (snap) => {
        const data = snap.data();
        if (!data || data.status === 'PENDING') return;
        clearTimeout(timeoutId);
        unsubscribe();
        if (data.status === 'SUCCESS') resolve(data);
        else reject(new Error(data.message || 'Failed to generate the document.'));
      }, (err) => {
        clearTimeout(timeoutId);
        unsubscribe();
        reject(err);
      });
    });
    deleteDoc(requestRef).catch(() => {});

    const byteChars = atob(result.base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    return bytes.buffer;
  };

  // Merges the template for every sid in resolvePrintSids() (or the single activity-level doc when
  // that list is [null]), fetching the template's raw bytes only once regardless of how many
  // participants are being produced.
  const mergeForPrintSids = async () => {
    const templateBytes = await fetchTemplateBytes(printTemplate.id);
    const sids = resolvePrintSids();
    const results = [];
    for (const sid of sids) {
      const values = buildMergeValues(printActivity, sid || '');
      const blob = await mergeDocxTemplate(templateBytes, values);
      const label = sid ? (studentMap[sid]?.name || sid) : null;
      results.push({ label, blob });
    }
    return results;
  };

  // Preview only ever shows the single selected participant (or activity-level) -- "All Participants"
  // has no single document to preview, so the Generate button is hidden for that selection (see JSX).
  const handleGeneratePrint = async () => {
    if (!printTemplate || !printActivity) return;
    setMerging(true);
    setPrintError('');
    setTemplatePreviewHtml('');
    setMergedBlob(null);
    try {
      const [{ blob }] = await mergeForPrintSids();
      setMergedBlob(blob);
      const mergedBuffer = await blob.arrayBuffer();
      const rendered = await mammoth.convertToHtml({ arrayBuffer: mergedBuffer });
      setTemplatePreviewHtml(rendered.value);
    } catch (e) {
      console.error('Error generating merged document', e);
      setPrintError(e.message || 'Failed to generate the document.');
    }
    setMerging(false);
  };

  // Self-contained: runs the full fetch+merge pipeline itself rather than depending on mergedBlob, so
  // clicking Download works directly without clicking Generate first.
  // Always downloads exactly one file, even for "All Participants" -- each participant's merged body
  // is combined into a single .docx (page break between each) rather than a .docx per participant.
  const handleDownloadMerged = async () => {
    if (!printTemplate || !printActivity) return;
    setDownloadingDocx(true);
    setPrintError('');
    try {
      const results = await mergeForPrintSids();
      const label = results.length > 1 ? 'All Participants' : results[0].label;
      const blob = await combineDocxBlobs(results);
      downloadBlob(blob, `${printTemplate.name}${label ? ' - ' + label : ''}.docx`);
    } catch (e) {
      console.error('Error downloading merged document', e);
      setPrintError(e.message || 'Failed to generate the document.');
    }
    setDownloadingDocx(false);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name.trim()) return alert(t('ole.templateNameRequired'));
    if (!editingTemplate && !templateForm.file) return alert(t('ole.chooseDocx'));
    setSavingTemplate(true);
    try {
      const templateId = editingTemplate ? editingTemplate.id : doc(collection(db, 'schools', activeSchoolId, 'ole_templates')).id;
      let { fileUrl = '', fileName = '', storagePath = '' } = editingTemplate || {};

      if (templateForm.file) {
        fileName = templateForm.file.name;
        storagePath = `upload/schools/${activeSchoolId}/ole_templates/${templateId}/${fileName}`;
        const fileRef = ref(storage, storagePath);
        const snapshot = await uploadBytesResumable(fileRef, templateForm.file);
        fileUrl = await getDownloadURL(snapshot.ref);
      }

      const payload = {
        name: templateForm.name.trim(),
        description: templateForm.description.trim(),
        fileName,
        storagePath,
        fileUrl,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      };
      if (!editingTemplate) {
        payload.createdAt = new Date().toISOString();
        payload.createdBy = auth.currentUser?.email || 'Unknown';
        Object.assign(payload, ownerStamp());
      }
      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_templates', templateId), payload, { merge: true });
      setShowTemplateModal(false);
      await fetchTemplates();
    } catch (e2) {
      console.error('Error saving template', e2);
      alert(t('ole.saveTemplateFailed', { message: e2.message }));
    }
    setSavingTemplate(false);
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(t('ole.deleteTemplateConfirm', { name: template.name }))) return;
    try {
      if (template.storagePath) {
        try { await deleteObject(ref(storage, template.storagePath)); } catch (e) { console.error('Error deleting template file from storage', e); }
      }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'ole_templates', template.id));
      await fetchTemplates();
    } catch (e) {
      console.error('Error deleting template', e);
      alert(t('ole.deleteTemplateFailed', { message: e.message }));
    }
  };

  // Plain navigation (not fetch+blob) deliberately — Firebase Storage's default bucket CORS config doesn't
  // allow script-initiated cross-origin reads from this app's origin, so a fetch() against the download URL
  // fails even though the URL itself is perfectly downloadable via a normal browser navigation/click.
  const handleDownloadTemplate = (template) => {
    if (!template?.fileUrl) return;
    window.open(template.fileUrl, '_blank', 'noopener');
  };

  const tabs = [
    { id: 'activity', label: t('ole.tabs.activity'), icon: Layers },
    { id: 'portfolio', label: t('ole.tabs.portfolio'), icon: BookOpen },
    { id: 'leaders', label: t('ole.tabs.leaders'), icon: Crown },
    { id: 'notices', label: t('ole.tabs.notices'), icon: ClipboardList },
    { id: 'template', label: t('ole.tabs.template'), icon: FileText },
    { id: 'settings', label: t('ole.tabs.settings'), icon: SettingsIcon },
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {activeTab === 'activity' && (
            <button className="btn-primary" onClick={openAddModal} disabled={!schoolYear} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <Plus size={14} /> {t('ole.addActivity')}
            </button>
          )}
          {activeTab === 'leaders' && (
            <button className="btn-primary" onClick={openAddLeaderModal} disabled={!schoolYear} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <Plus size={14} /> Add Leader
            </button>
          )}
          {activeTab === 'activity' && (
            <>
              <button type="button" className="btn-outline" onClick={() => fileInputRef.current?.click()} disabled={importing} title={importing ? t('ole.importing') : t('ole.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                <Upload size={16} />
              </button>
              <button type="button" className="btn-outline" onClick={handleExportCSV} disabled={activities.length === 0} title={t('ole.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                <Download size={16} />
              </button>
            </>
          )}
          {activeTab === 'template' && (
            <button className="btn-primary" onClick={openCreateTemplateModal} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <Plus size={14} /> Create Template
            </button>
          )}
          {activeTab === 'portfolio' && (
            <>
              <select
                className="input-field"
                value={portfolioClass}
                onChange={e => { setPortfolioClass(e.target.value); setPortfolioStudentSid(''); }}
                style={{ width: '160px' }}
              >
                <option value="">-- Select Class --</option>
                {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                className="input-field"
                value={portfolioStudentSid}
                onChange={e => setPortfolioStudentSid(e.target.value)}
                disabled={!portfolioClass}
                style={{ width: '280px' }}
              >
                <option value="">-- Select Student --</option>
                {portfolioClassStudents.map(s => <option key={s.sid} value={s.sid}>{s.classNumber} - {s.name} {s.chineseName}</option>)}
              </select>
            </>
          )}
          <select className="input-field" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={{ width: '140px' }}>
            {academicYears.length === 0 ? (
              <option value="">{t('ole.noYearsConfigured')}</option>
            ) : academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearId}</option>)}
          </select>
        </div>
        </HeaderActionsPortal>
      </div>

      <input type="file" ref={fileInputRef} accept=".csv" style={{ display: 'none' }} onChange={handleImportFile} />

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>
        {activeTab === 'activity' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%', minHeight: 0 }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>OLE Activities {schoolYear && `(${schoolYear})`}</h2>

            {/* Scrolls in BOTH directions and is bounded to the visible area. Previously it only
                scrolled horizontally and grew to the full table height, which pushed its horizontal
                scrollbar hundreds of pixels below the fold -- you had to scroll to the end of the
                list before you could reach the last columns. */}
            <div className="table-container" style={{ flex: 1, minHeight: 0, overflow: 'auto', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)', position: 'sticky', top: 0, zIndex: 2 }}>
                    {activityColumns.map(col => (
                      <th key={col.key} style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          {col.label}
                          <ColumnMenu
                            label={col.label}
                            filterValue={columnFilters[col.key]}
                            onFilterChange={(val) => setColumnFilters(prev => ({ ...prev, [col.key]: val }))}
                            sortDirection={sortConfig.key === col.key ? sortConfig.direction : null}
                            onSort={(direction) => setColumnSort(col.key, direction)}
                          />
                        </div>
                      </th>
                    ))}
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '170px' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingActivities ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : displayActivities.length === 0 ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{activities.length === 0 ? 'No activities recorded for this school year.' : 'No activities match the current filters.'}</td></tr>
                  ) : displayActivities.map((a, i) => (
                    <tr key={a.id} style={{ borderBottom: i === displayActivities.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{a.id}</td>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>
                        {a.titleEn || a.titleZh}
                        {a.titleEn && a.titleZh && <div style={{ fontWeight: '400', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{a.titleZh}</div>}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {a.activityDate || '-'}
                        {(a.timeStart || a.timeEnd) && <div style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{a.timeStart || '?'} - {a.timeEnd || '?'}</div>}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>{bilingualLabel(organizationMap[a.organization]) || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{bilingualLabel(natureMap[a.nature]) || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{a.staffInCharge || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <button onClick={() => openParticipantModal(a)} className="btn-outline" style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', height: 'auto' }}>
                          <Users size={14} /> {(a.studentInvolved || []).length}
                        </button>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => openRoleModal(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('ole.participantRole')}>
                            <Award size={16} />
                          </button>
                          <button onClick={() => openPrintModalForActivity(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('ole.printDocument')}>
                            <Printer size={16} />
                          </button>
                          <button onClick={() => openAttendanceModal(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('ole.takeAttendance')}>
                            <ClipboardCheck size={16} />
                          </button>
                          <button onClick={() => openEditModal(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('common.edit')}>
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => { setNoticeSource(a); setNoticeContent(''); setNoticeIssuer(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }} title={t('ole.issueNotice')}>
                            <FileText size={16} />
                          </button>
                          <button onClick={() => { setCopySource(a); setCopyParticipants(true); setCopyRoles(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }} title={t('ole.copyActivity')}>
                            <Copy size={16} />
                          </button>
                          <button onClick={() => handleDeleteActivity(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'portfolio' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {!portfolioStudentSid ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('ole.pickStudent')}</div>
            ) : loadingPortfolio ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
            ) : portfolioGroups.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('ole.noPortfolio')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0 1.25rem', fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>
                  <div style={{ width: '100px', flexShrink: 0 }}>{t('common.date')}</div>
                  <div style={{ flex: '1 1 240px', minWidth: 0 }}>{t('ole.activity')}</div>
                  <div style={{ width: '170px', flexShrink: 0 }}>{t('ole.nature')}</div>
                  <div style={{ width: '130px', flexShrink: 0 }}>{t('ole.role')}</div>
                  <div style={{ flex: '1 1 160px', minWidth: 0 }}>{t('ole.achievement')}</div>
                  <div style={{ width: '16px', flexShrink: 0 }} />
                </div>

                {portfolioGroups.map(group => (
                  <div key={group.schoolYear}>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: '700', margin: '0 0 0.75rem' }}>{group.schoolYear}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {group.items.map(a => {
                        const isExpanded = expandedPortfolioIds.has(a.id);
                        const detail = a.participantDetails?.[portfolioStudentSid];
                        const natureInfo = natureMap[a.nature];
                        const roleInfo = detail?.role ? roleMap[detail.role] : null;
                        return (
                          <div key={a.id} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
                            <div
                              onClick={() => togglePortfolioExpand(a.id)}
                              style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem' }}
                            >
                              <div style={{ width: '100px', flexShrink: 0, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{a.activityDate || '-'}</div>
                              <div style={{ flex: '1 1 240px', minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.titleEn || '-'}</div>
                                {a.titleZh && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.titleZh}</div>}
                              </div>
                              <div style={{ width: '170px', flexShrink: 0, overflow: 'hidden' }}>
                                <div style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{natureInfo?.nameEn || '-'}</div>
                                {natureInfo?.nameZh && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{natureInfo.nameZh}</div>}
                              </div>
                              <div style={{ width: '130px', flexShrink: 0, overflow: 'hidden' }}>
                                <div style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleInfo?.nameEn || '-'}</div>
                                {roleInfo?.nameZh && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleInfo.nameZh}</div>}
                              </div>
                              <div style={{ flex: '1 1 160px', minWidth: 0, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail?.achievement || '-'}</div>
                              <div style={{ width: '16px', flexShrink: 0, color: 'var(--color-text-muted)', display: 'flex' }}>
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </div>
                            </div>
                            {isExpanded && (
                              <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '1rem', rowGap: '0.5rem', marginTop: '1rem' }}>
                                  <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.activityId')}:</div>
                                  <div>{a.id}</div>
                                  <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.dateTime')}:</div>
                                  <div>{a.activityDate || '-'} {a.timeStart || '?'} - {a.timeEnd || '?'}</div>
                                  <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.organization')}:</div>
                                  <div>{bilingualLabel(organizationMap[a.organization]) || '-'}</div>
                                  <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.venue')}:</div>
                                  <div>{[a.venueEn, a.venueZh].filter(Boolean).join(' / ') || '-'}</div>
                                  <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.staffInCharge')}:</div>
                                  <div>{a.staffInCharge || '-'}</div>
                                  {a.accompanyStaff?.length > 0 && (
                                    <>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.accompanyingStaff')}:</div>
                                      <div>{a.accompanyStaff.join(', ')}</div>
                                    </>
                                  )}
                                  {(a.descriptionEn || a.descriptionZh) && (
                                    <>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('ole.descriptionLabel')}:</div>
                                      <div>
                                        {a.descriptionEn}
                                        {a.descriptionEn && a.descriptionZh && ' / '}
                                        {a.descriptionZh}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'leaders' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>Student Leaders {schoolYear && `(${schoolYear})`}</h2>

            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    {leaderColumns.map(col => (
                      <th key={col.key} style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          {col.label}
                          <ColumnMenu
                            label={col.label}
                            filterValue={leaderColumnFilters[col.key]}
                            onFilterChange={(val) => setLeaderColumnFilters(prev => ({ ...prev, [col.key]: val }))}
                            sortDirection={leaderSortConfig.key === col.key ? leaderSortConfig.direction : null}
                            onSort={(direction) => setLeaderColumnSort(col.key, direction)}
                          />
                        </div>
                      </th>
                    ))}
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '90px' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLeaders ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : displayLeaders.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{leaderRecords.length === 0 ? 'No student leaders recorded for this school year.' : 'No leaders match the current filters.'}</td></tr>
                  ) : displayLeaders.map((r, i) => {
                    const st = studentMap[r.sid];
                    return (
                      <tr key={r.id} style={{ borderBottom: i === displayLeaders.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{bilingualLabel(organizationMap[r.organization]) || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{bilingualLabel(leaderRoleMap[r.role]) || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{st ? `${st.name} ${st.chineseName}` : r.sid}</td>
                        <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{st ? `${st.currentClass} (${st.classNumber})` : '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => openEditLeaderModal(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('common.edit')}>
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDeleteLeader(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'notices' ? (
          <OleNotices
            activeSchoolId={activeSchoolId}
            studentMap={studentMap}
            isMobile={isMobile}
            canManage={canManageOle}
          />
        ) : activeTab === 'template' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('ole.templatesTitle')}</h2>

            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.name')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('ole.descriptionLabel')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('ole.file')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('ole.lastUpdated')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '140px' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTemplates ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : templates.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>No templates yet. Click "Create Template" to add one.</td></tr>
                  ) : templates.map((tpl, i) => (
                    <tr key={tpl.id} style={{ borderBottom: i === templates.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{tpl.name}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{tpl.description || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{tpl.fileName || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleDateString() : '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => openPrintModal(tpl)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('ole.print')}>
                            <Printer size={16} />
                          </button>
                          <button onClick={() => handleDownloadTemplate(tpl)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('ole.download')}>
                            <Download size={16} />
                          </button>
                          <button onClick={() => openEditTemplateModal(tpl)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('common.edit')}>
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDeleteTemplate(tpl)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" onClick={handleSaveSettings} disabled={savingSettings || !settingsLoaded} style={{ padding: '0.5rem 1.25rem' }}>
                {savingSettings ? t('common.saving') : t('ole.saveSettings')}
              </button>
            </div>

            {/* Its own approvers, pointed at its own settings document. A school that wants the ECA
                coordinator signing activity notices and the office signing general ones can say so
                here without either list leaking into the other. */}
            <NoticeApprovalSettings
              schoolId={activeSchoolId}
              staff={staffList}
              canManage={canManageOle}
              isMobile={isMobile}
              settingsDoc="ole_notice_settings"
            />

            <ListEditor
              isMobile={isMobile}
              title={t('ole.organizations')}
              items={organizations}
              onChange={setOrganizations}
              fields={[
                { key: 'code', label: t('ole.code'), placeholder: t('ole.example', { value: 'IT_COM' }) },
                { key: 'nameEn', label: t('ole.nameEn'), placeholder: t('ole.example', { value: 'IT Committee' }) },
                { key: 'nameZh', label: t('ole.nameZh'), placeholder: t('ole.example', { value: '資訊科技組' }) },
                { key: 'teacherInCharge', label: t('ole.teacherInCharge'), type: 'select', options: staffOptions },
              ]}
              headerExtra={
                importCandidates.length > 0 && (
                  <button type="button" className="btn-outline" onClick={handleImportOrganizations} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', whiteSpace: 'nowrap' }}>
                    <Download size={16} /> {t('ole.importFromDepartments')}
                  </button>
                )
              }
            />

            <ListEditor
              isMobile={isMobile}
              title={t('ole.natureTitle')}
              items={natures}
              onChange={setNatures}
              fields={[
                { key: 'code', label: t('ole.code'), placeholder: t('ole.example', { value: 'COMM_SVC' }) },
                { key: 'nameEn', label: t('ole.nameEn'), placeholder: t('ole.example', { value: 'Community Service' }) },
                { key: 'nameZh', label: t('ole.nameZh'), placeholder: t('ole.example', { value: '社會服務' }) },
              ]}
            />

            <ListEditor
              isMobile={isMobile}
              title={t('ole.participantRoles')}
              items={participantRoles}
              onChange={setParticipantRoles}
              fields={[
                { key: 'code', label: t('ole.code'), placeholder: t('ole.example', { value: 'LEADER' }) },
                { key: 'nameEn', label: t('ole.nameEn'), placeholder: t('ole.example', { value: 'Student Leader' }) },
                { key: 'nameZh', label: t('ole.nameZh'), placeholder: t('ole.example', { value: '學生領袖' }) },
              ]}
            />

            <ListEditor
              isMobile={isMobile}
              title={t('ole.leadersRole')}
              items={leaderRoles}
              onChange={setLeaderRoles}
              fields={[
                { key: 'code', label: t('ole.code'), placeholder: t('ole.example', { value: 'PREFECT' }) },
                { key: 'nameEn', label: t('ole.nameEn'), placeholder: t('ole.example', { value: 'Prefect' }) },
                { key: 'nameZh', label: t('ole.nameZh'), placeholder: t('ole.example', { value: '風紀' }) },
              ]}
            />
          </div>
        )}
      </div>

      {/* Add / Edit Activity Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '960px', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              {editingId ? t('ole.editActivity', { id: editingId }) : t('ole.addActivity')}
            </h3>

            <form onSubmit={handleSaveActivity} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.titleEn')}</label>
                  <input type="text" className="input-field" value={form.titleEn} onChange={e => setForm({ ...form, titleEn: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.titleZh')}</label>
                  <input type="text" className="input-field" value={form.titleZh} onChange={e => setForm({ ...form, titleZh: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.descriptionEn')}</label>
                  <textarea className="input-field" rows={3} value={form.descriptionEn} onChange={e => setForm({ ...form, descriptionEn: e.target.value })} style={{ resize: 'vertical', height: 'auto', minHeight: '80px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.descriptionZh')}</label>
                  <textarea className="input-field" rows={3} value={form.descriptionZh} onChange={e => setForm({ ...form, descriptionZh: e.target.value })} style={{ resize: 'vertical', height: 'auto', minHeight: '80px' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.organization')}</label>
                  <select className="input-field" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })}>
                    <option value="">-- Select --</option>
                    {sortedOrganizations.map(o => <option key={o.code} value={o.code}>{bilingualLabel(o)}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.nature')}</label>
                  <select className="input-field" value={form.nature} onChange={e => setForm({ ...form, nature: e.target.value })}>
                    <option value="">-- Select --</option>
                    {sortedNatures.map(n => <option key={n.code} value={n.code}>{bilingualLabel(n)}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.dates')}</label>
                  <SessionDatePicker
                    dates={form.sessionDates || []}
                    onChange={(sessionDates) => setForm({ ...form, sessionDates })}
                    lockedDates={lockedSessionDates}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.startTime')}</label>
                  <input type="time" className="input-field" value={form.timeStart} onChange={e => setForm({ ...form, timeStart: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.endTime')}</label>
                  <input type="time" className="input-field" value={form.timeEnd} onChange={e => setForm({ ...form, timeEnd: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.venueEn')}</label>
                  <input type="text" className="input-field" value={form.venueEn} onChange={e => setForm({ ...form, venueEn: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.venueZh')}</label>
                  <input type="text" className="input-field" value={form.venueZh} onChange={e => setForm({ ...form, venueZh: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.staffInCharge')}</label>
                  <select className="input-field" value={form.staffInCharge} onChange={e => setForm({ ...form, staffInCharge: e.target.value })}>
                    <option value="">-- Select --</option>
                    {staffOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.accompanyingStaff')}</label>
                  <TagPicker
                    items={staffList}
                    selected={form.accompanyStaff}
                    onChange={(next) => setForm({ ...form, accompanyStaff: next })}
                    labelFn={s => `${s.name} (${s.initial})`}
                    keyFn={s => s.initial}
                    placeholder={t('ole.searchStaff')}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.submitReplyTo')}</label>
                  <select className="input-field" value={form.replyToStaff} onChange={e => setForm({ ...form, replyToStaff: e.target.value })}>
                    <option value="">-- Select --</option>
                    {staffOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.submitByDate')}</label>
                  <input type="date" className="input-field" value={form.replyDueDate} onChange={e => setForm({ ...form, replyDueDate: e.target.value })} />
                </div>
              </div>

              {!editingId && (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0 }}>
                  After saving, you'll be able to add participating students in the next step.
                </p>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? t('common.saving') : (editingId ? t('ole.saveChanges') : t('ole.saveAndAddParticipants'))}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Participants Modal (separate step, per the requested search + class filter + multi-select flow) */}
      {showParticipantModal && participantActivity && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 110, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '700px', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>
              Manage Participants
            </h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              {participantActivity.titleEn || participantActivity.titleZh} ({participantActivity.id}) — {participantSelected.length} student(s) selected
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <select className="input-field" value={participantClassFilter} onChange={e => setParticipantClassFilter(e.target.value)} style={{ width: '140px', flexShrink: 0 }}>
                <option value="">{t('ole.allClasses')}</option>
                {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="text"
                className="input-field"
                placeholder={t('ole.searchStudents')}
                value={participantSearch}
                onChange={e => setParticipantSearch(e.target.value)}
                style={{ flex: 1 }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.5rem', cursor: filteredParticipantStudents.length === 0 ? 'default' : 'pointer', color: filteredParticipantStudents.length === 0 ? 'var(--color-text-muted)' : 'inherit' }}>
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAllFiltered}
                disabled={filteredParticipantStudents.length === 0}
                style={{ width: '15px', height: '15px', cursor: 'pointer' }}
              />
              Select All {participantClassFilter ? `in ${participantClassFilter}` : '(Filtered)'} ({filteredParticipantStudents.length})
            </label>

            <div style={{ flex: 1, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', maxHeight: '360px', overflowY: 'auto' }}>
              {filteredParticipantStudents.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('ole.noMatchingStudents')}</div>
              ) : filteredParticipantStudents.map(s => (
                <label key={s.sid} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={participantSelected.includes(s.sid)}
                    onChange={() => toggleParticipant(s.sid)}
                    style={{ width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                  />
                  <span style={{ color: 'var(--color-text-muted)', width: '70px', flexShrink: 0 }}>{s.currentClass} ({s.classNumber})</span>
                  <span>{s.name} {s.chineseName}</span>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.25rem' }}>
              <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowParticipantModal(false)}>{t('common.cancel')}</button>
              <button type="button" className="btn-primary" style={{ flex: 1 }} onClick={handleSaveParticipants} disabled={savingParticipants}>
                {savingParticipants ? t('common.saving') : t('ole.saveParticipants')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Participant Role Modal — opened from the Actions column, not a separate tab */}
      {showRoleModal && roleActivity && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 110, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '760px', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>
              Participant Role
            </h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              {roleActivity.titleEn || roleActivity.titleZh} ({roleActivity.id})
            </p>

            <div className="table-container" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('common.className')}</th>
                    <th style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('common.student')}</th>
                    <th style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('ole.role')}</th>
                    <th style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('ole.achievementAward')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(roleAssignments).length === 0 ? (
                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('ole.noParticipantsYet')}</td></tr>
                  ) : Object.keys(roleAssignments).map((sid, i, arr) => {
                    const s = studentMap[sid];
                    return (
                      <tr key={sid} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)' }}>{s ? `${s.currentClass} (${s.classNumber})` : '-'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', fontWeight: '600' }}>{s ? `${s.name} ${s.chineseName}` : sid}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <select
                            className="input-field"
                            value={roleAssignments[sid]?.role || ''}
                            onChange={e => updateRoleAssignment(sid, 'role', e.target.value)}
                            style={{ minWidth: '150px' }}
                          >
                            <option value="">-- Select --</option>
                            {sortedParticipantRoles.map(r => <option key={r.code} value={r.code}>{bilingualLabel(r)}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <input
                            type="text"
                            className="input-field"
                            placeholder="e.g. Champion, Best Actor..."
                            value={roleAssignments[sid]?.achievement || ''}
                            onChange={e => updateRoleAssignment(sid, 'achievement', e.target.value)}
                            style={{ minWidth: '200px' }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.25rem' }}>
              <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => { setShowRoleModal(false); setRoleActivity(null); }}>{t('common.cancel')}</button>
              <button type="button" className="btn-primary" style={{ flex: 1 }} onClick={handleSaveRoleAssignments} disabled={savingRoleAssignments}>
                {savingRoleAssignments ? t('common.saving') : t('ole.saveRoles')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Leader Modal */}
      {showLeaderModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '480px', maxHeight: isMobile ? '92vh' : 'none', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              {t(editingLeaderId ? 'ole.editLeader' : 'ole.addLeader')}
            </h3>

            <form onSubmit={handleSaveLeader} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.organization')}</label>
                <select className="input-field" value={leaderForm.organization} onChange={e => setLeaderForm({ ...leaderForm, organization: e.target.value })}>
                  <option value="">-- Select --</option>
                  {sortedOrganizations.map(o => <option key={o.code} value={o.code}>{bilingualLabel(o)}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.role')}</label>
                <select className="input-field" value={leaderForm.role} onChange={e => setLeaderForm({ ...leaderForm, role: e.target.value })}>
                  <option value="">-- Select --</option>
                  {sortedLeaderRoles.map(r => <option key={r.code} value={r.code}>{bilingualLabel(r)}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('common.className')}</label>
                  <select
                    className="input-field"
                    value={leaderClassFilter}
                    onChange={e => { setLeaderClassFilter(e.target.value); setLeaderForm(prev => ({ ...prev, sid: '' })); }}
                  >
                    <option value="">-- Select Class --</option>
                    {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('common.student')}</label>
                  <select
                    className="input-field"
                    value={leaderForm.sid}
                    onChange={e => setLeaderForm({ ...leaderForm, sid: e.target.value })}
                    disabled={!leaderClassFilter}
                  >
                    <option value="">-- Select Student --</option>
                    {leaderClassStudents.map(s => <option key={s.sid} value={s.sid}>{s.classNumber} - {s.name} {s.chineseName}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowLeaderModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingLeader}>{savingLeader ? t('common.saving') : t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create / Edit Template Modal */}
      {showTemplateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '520px', maxHeight: isMobile ? '92vh' : 'none', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              {t(editingTemplate ? 'ole.editTemplate' : 'ole.createTemplate')}
            </h3>

            <form onSubmit={handleSaveTemplate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.templateName')}</label>
                <input type="text" className="input-field" value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('ole.descriptionOptional')}</label>
                <textarea className="input-field" rows={2} value={templateForm.description} onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })} style={{ resize: 'vertical', height: 'auto', minHeight: '60px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {t(editingTemplate ? 'ole.replaceFile' : 'ole.uploadDocx')}
                </label>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem', padding: '0.6rem 0.75rem', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-md)' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('ole.sampleHint')}</span>
                  <button type="button" className="btn-outline" onClick={() => handleDownloadSampleTemplate(t)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', padding: '0.35rem 0.7rem', whiteSpace: 'nowrap' }}>
                    <Download size={14} /> {t('ole.downloadSample')}
                  </button>
                </div>
                <input
                  type="file"
                  accept=".docx"
                  onChange={e => setTemplateForm({ ...templateForm, file: e.target.files?.[0] || null })}
                />
                {editingTemplate?.fileName && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.4rem 0 0' }}>
                    Current file: {editingTemplate.fileName} — leave blank to keep it.
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowTemplateModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingTemplate}>{savingTemplate ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAttendanceModal && attendanceActivity && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 110, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '760px', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>{t('ole.attendanceTitle')}</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: 0, marginBottom: '1rem' }}>
              {t('ole.attendanceSubtitle', { title: attendanceActivity.title })}
            </p>

            {/* One register per day. Switching day loads that day's register, so a five-day activity
                has five independent registers rather than one shared answer. */}
            {attendanceSessions.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--color-danger)', marginTop: 0, marginBottom: '1rem' }}>
                {t('ole.noDatesYet')}
              </p>
            ) : (
              <div style={{ marginBottom: '1rem' }}>
                <label className="input-label">{t('ole.session')}</label>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {attendanceSessions.map((se) => {
                    const active = se.id === attendanceSessionId;
                    return (
                      <button
                        key={se.id}
                        type="button"
                        onClick={() => loadSessionRegister(attendanceActivity, se.id)}
                        style={{
                          padding: '0.3rem 0.7rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-border)',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          backgroundColor: active ? 'var(--color-primary)' : 'transparent',
                          color: active ? '#fff' : 'var(--color-text-main)',
                        }}
                      >
                        {se.date}
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: '0.4rem 0 0' }}>
                  Saving records this day only. The other days keep whatever was recorded for them.
                </p>
              </div>
            )}

            {(() => {
              const sids = Array.isArray(attendanceActivity.studentInvolved) ? attendanceActivity.studentInvolved : [];
              const counts = ATTENDANCE_STATES.map(st => ({
                ...st,
                n: sids.filter(sid => (attendanceMap[sid] || 'pending') === st.id).length,
              }));
              return (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.85rem' }}>
                  {counts.map(c => (
                    <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: c.colour, fontWeight: '700' }}>
                      <c.icon size={15} /> {c.n} {c.label.toLowerCase()}
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => setAttendanceMap(Object.fromEntries(sids.map(sid => [sid, 'present'])))}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontWeight: '600', fontSize: '0.8rem' }}
                  >
                    Mark all present
                  </button>
                </div>
              );
            })()}

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', position: 'sticky', top: 0, zIndex: 1, textAlign: 'left', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    <th style={{ padding: '0.6rem 0.75rem' }}>{t('common.className')}</th>
                    <th style={{ padding: '0.6rem 0.75rem' }}>{t('ole.classNo')}</th>
                    <th style={{ padding: '0.6rem 0.75rem' }}>{t('common.name')}</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Present?</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(attendanceActivity.studentInvolved) ? attendanceActivity.studentInvolved : []).map(sid => {
                    const st = studentMap[sid];
                    const current = attendanceMap[sid] || 'pending';
                    return (
                      <tr key={sid} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{st?.currentClass || '-'}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{st?.classNumber || '-'}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{st?.name || sid}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
                            {ATTENDANCE_STATES.map(state => {
                              const active = current === state.id;
                              const Icon = state.icon;
                              return (
                                <button
                                  key={state.id}
                                  type="button"
                                  title={state.label}
                                  aria-label={`${st?.name || sid}: ${state.label}`}
                                  aria-pressed={active}
                                  onClick={() => setAttendanceMap(prev => ({ ...prev, [sid]: state.id }))}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: '30px', height: '30px', borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    border: `1px solid ${active ? state.colour : 'var(--color-border)'}`,
                                    backgroundColor: active ? state.colour : 'transparent',
                                    color: active ? '#fff' : state.colour,
                                  }}
                                >
                                  <Icon size={16} />
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" className="btn-outline" onClick={() => setShowAttendanceModal(false)} disabled={savingAttendance} style={{ width: 'auto', padding: '0.55rem 1rem' }}>{t('common.cancel')}</button>
              <button type="button" className="btn-primary" onClick={handleSaveAttendance} disabled={savingAttendance} style={{ width: 'auto', padding: '0.55rem 1rem' }}>
                {savingAttendance ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrintModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '900px', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>Print{printTemplate ? `: ${printTemplate.name}` : ' Document'}</h3>
              <button type="button" onClick={() => setShowPrintModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('ole.tabs.template')}</label>
                <select className="input-field" value={printTemplate?.id || ''} onChange={e => { setPrintTemplate(templates.find(tpl => tpl.id === e.target.value) || null); setMergedBlob(null); setTemplatePreviewHtml(''); setPrintError(''); }}>
                  <option value="">{t('ole.selectTemplate')}</option>
                  {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 260px', minWidth: '220px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('ole.activity')}</label>
                <select className="input-field" value={printActivityId} onChange={e => { setPrintActivityId(e.target.value); setPrintParticipantSid(''); setMergedBlob(null); setTemplatePreviewHtml(''); setPrintError(''); }}>
                  <option value="">-- Select an activity --</option>
                  {activities.map(a => <option key={a.id} value={a.id}>{a.titleEn} ({a.activityDate})</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('ole.participantOptional')}</label>
                <select className="input-field" value={printParticipantSid} onChange={e => { setPrintParticipantSid(e.target.value); setMergedBlob(null); setTemplatePreviewHtml(''); setPrintError(''); }} disabled={!printActivity || printParticipants.length === 0}>
                  <option value="">-- Activity-level only --</option>
                  {printParticipants.length > 0 && (
                    <option value={ALL_PARTICIPANTS_VALUE}>-- All Participants ({printParticipants.length}) --</option>
                  )}
                  {printParticipants.map(s => <option key={s.sid} value={s.sid}>{s.name} {s.chineseName} ({s.currentClass})</option>)}
                </select>
              </div>
              {printParticipantSid !== ALL_PARTICIPANTS_VALUE && (
                <button type="button" className="btn-primary" onClick={handleGeneratePrint} disabled={!printTemplate || !printActivity || merging} style={{ padding: '0.55rem 1.25rem', whiteSpace: 'nowrap' }}>
                  {merging ? 'Generating...' : 'Generate Preview'}
                </button>
              )}
            </div>

            {printError && (
              <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', marginBottom: '1rem' }}>{printError}</p>
            )}

            {printParticipantSid === ALL_PARTICIPANTS_VALUE ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
                Preview isn't available when generating for all participants at once — use Download below directly; every participant's document will be combined into one .docx (with a page break between each), not a separate file per participant.
              </p>
            ) : templatePreviewHtml && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <small style={{ fontWeight: '700', textTransform: 'uppercase', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('ole.preview')}</small>
                </div>
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '1.5rem', maxHeight: '500px', overflowY: 'auto', backgroundColor: '#fff', color: '#111', whiteSpace: 'pre-wrap' }}>
                  <div dangerouslySetInnerHTML={{ __html: templatePreviewHtml }} />
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                  Approximate rendering of the merged document — layout/pagination may differ slightly from Word.
                </p>
              </>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="button" className="btn-outline" style={{ flex: 1 }} disabled={!printTemplate || !printActivity || downloadingDocx} onClick={handleDownloadMerged}>
                <Download size={16} style={{ marginRight: '0.4rem', verticalAlign: 'text-bottom' }} /> {downloadingDocx ? t('ole.downloading') : t('ole.downloadDocx')}
              </button>
              <button type="button" className="btn-primary" style={{ flex: 1 }} onClick={() => setShowPrintModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
      {noticeSource && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{t('ole.issueNoticeTitle')}</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0.4rem 0 0', lineHeight: 1.7 }}>
                {t('ole.noticeIntro')}
              </p>
            </div>

            <div style={{ padding: '0.7rem 0.85rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', fontSize: '0.85rem', lineHeight: 1.8 }}>
              <div><strong>{noticeSource.titleEn || noticeSource.titleZh || noticeSource.activityId}</strong></div>
              <div style={{ color: 'var(--color-text-muted)' }}>
                {t('ole.participantCount', { count: (noticeSource.studentInvolved || []).length })}
              </div>
              <div style={{ marginTop: '0.4rem' }}>
                {(noticeSource.sessionDates || []).length === 0 ? (
                  <span style={{ color: 'var(--color-danger)' }}>
                    {t('ole.noDatesWarning')}
                  </span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {[...noticeSource.sessionDates].sort().map(d => (
                      <span key={d} style={{ fontSize: '0.78rem', padding: '0.12rem 0.4rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
                        {d}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="input-label">{t('ole.issuedBy')}</label>
              <select className="input-field" value={noticeIssuer} onChange={e => setNoticeIssuer(e.target.value)} style={{ margin: 0 }}>
                <option value="">{t('ole.selectPlaceholder')}</option>
                {staffList.map(st => (
                  <option key={st.initial} value={st.initial}>
                    {st.initial}{st.name ? ` — ${st.name}` : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0' }}>
                Who the notice is <em>from</em> — often the Principal. Who pressed the button is
                recorded separately.
              </p>
            </div>

            <div>
              <label className="input-label">{t('ole.content')}</label>
              <textarea
                className="input-field"
                rows={6}
                value={noticeContent}
                onChange={e => setNoticeContent(e.target.value)}
                placeholder={t('ole.contentPlaceholder')}
                style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }}
              />
              {noticeTemplates.filter(nt => (nt.appliesTo || 'both') !== 'general').length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    {t('ole.startFromTemplate')}
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.3rem' }}>
                    {noticeTemplates
                      .filter(nt => (nt.appliesTo || 'both') !== 'general')
                      .map(nt => (
                        <button
                          key={nt.id}
                          type="button"
                          onClick={() => {
                            if (noticeContent.trim() &&
                                !window.confirm(t('ole.replaceWithTemplate'))) return;
                            setNoticeContent(fillTemplate(nt.body, noticeValues({
                              title: noticeSource.titleEn || noticeSource.titleZh || '',
                              dates: noticeSource.sessionDates || [],
                              time: [noticeSource.timeStart, noticeSource.timeEnd].filter(Boolean).join(' – '),
                              venue: noticeSource.venueEn || noticeSource.venueZh || '',
                              replyDueDate: noticeSource.replyDueDate || '',
                            })));
                          }}
                          style={{ fontSize: '0.78rem', padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', backgroundColor: 'transparent', cursor: 'pointer' }}
                        >
                          {nt.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button className="btn-outline" onClick={() => setNoticeSource(null)} disabled={issuingNotice}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleIssueNotice} disabled={issuingNotice}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                {issuingNotice ? 'Submitting…' : 'Submit for approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {copySource && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', width: '100%', maxWidth: '460px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{t('ole.copyActivityTitle')}</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0.4rem 0 0', lineHeight: 1.7 }}>
                {t('ole.copyFrom', { title: copySource.titleEn || copySource.titleZh || copySource.activityId })}
              </p>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={copyParticipants}
                     onChange={(e) => { setCopyParticipants(e.target.checked); if (!e.target.checked) setCopyRoles(false); }}
                     style={{ width: '16px', height: '16px', marginTop: '0.2rem', accentColor: 'var(--color-primary)' }} />
              <span style={{ fontSize: '0.9rem' }}>{t('ole.copyParticipants')}</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: copyParticipants ? 'pointer' : 'default', opacity: copyParticipants ? 1 : 0.45 }}>
              <input type="checkbox" checked={copyRoles} disabled={!copyParticipants}
                     onChange={(e) => setCopyRoles(e.target.checked)}
                     style={{ width: '16px', height: '16px', marginTop: '0.2rem', accentColor: 'var(--color-primary)' }} />
              <span style={{ fontSize: '0.9rem' }}>
                {t('ole.copyRoles')}
                <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                  {t('ole.copyRolesHint')}
                </span>
              </span>
            </label>

            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.7 }}>
              <strong>{t('ole.notCopiedLabel')}</strong>{t('ole.notCopiedDetail')}
            </p>

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button className="btn-outline" onClick={() => setCopySource(null)} disabled={copying}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleCopyActivity} disabled={copying}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                {copying ? t('ole.copying') : t('ole.copy')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OLEManagement;
